import { FaLayerGroup, FaBookOpen, FaListUl } from 'react-icons/fa';
import { toast } from 'react-toastify';
// =====================================================================
// ==================== IMPORTS ET CONFIGURATION =======================
// =====================================================================
import ConversationAssistant from '../components/ui/ConversationAssistant';
import ImpactAnalyzer from '../components/ui/ImpactAnalyzer';
import PendingNodesPanel from "./PendingNodesPanel";
import QualityAssistant from "./VectorSearch/QualityAssistant";
import CreateNodeSection from './CreateNodeSection';
import ManualTestCreator from './ManualTestCreator';
import NodesPagination from "./NodesPagination";
import RelationsPagination from "./RelationsPagination";
import NodesList from "./NodesList";
import RelationsList from "./RelationsList";
import NodementedFunnel from './ui/NodementedFunnel.js';
import './ui/NodementedFunnelIntegration.css';
import GlobalTestsGraph from "./GlobalTestsGraph";
import GlobalTestsSection from "./GlobalTestsSection";
import { TemplateLibraryModal, TemplateLibraryButton } from './TemplateLibraryModal';
import TestCoverageTable from "./TestCoverageTable";
import TestScenariosList from "./TestScenariosList";
import TestScenarioDetail from "./TestScenarioDetail";
import CopyButton from "./ui/CopyButton";
import LLMResponseHeader from "./ui/LLMResponseHeader";
import ScenarioTypeBadge from "./ui/ScenarioTypeBadge";
import TemplateGuide from "./ui/TemplateGuide";
import SpecificationsSection from './SpecificationsSection';
import PreviewNode from "./PreviewNode";
import RelationCreator from "./RelationCreator";
import EnrichmentModal from "./EnrichmentModal";
import ConflictResolutionModal from "./modals/ConflictResolutionModal"; // 🔍 NOUVEAU - Modal de résolution de conflits
import { useConflictResolution } from '../hooks/useConflictResolution'; // 🔍 NOUVEAU - Hook de gestion des conflits
import NodeForm from "./NodeForm";
import SelectedNodeCartouche from "./SelectedNodeCartouche";
import ImportCodeModal from "./modals/ImportCodeModal";
import QuickCreateModal from "./modals/QuickCreateModal";
import JiraAnalyzerModal from "./integrations/JiraAnalyzerModal";
import IntegrationPlaceholder from "./integrations/IntegrationPlaceholder";
import IntegrationSetupModal from "./integrations/IntegrationSetupModal";
import { useIntegrations } from '../hooks/useIntegrations';
import TestCycleManager from "./zephyrmented/TestCycleManager";
import ExploratoryTestRunner from "./ExploratoryTestRunner"; // 🤖 NOUVEAU - Tests exploratoires FARA-7B
import RegressionRadar from "./RegressionRadar"; // 📡 NOUVEAU - Regression Radar
import SmartDuplicateDetector from "./smartTools/SmartDuplicateDetector";
import TestCoverageAdvisor from "./smartTools/TestCoverageAdvisor";
import RelatedFeaturesFinder from "./smartTools/RelatedFeaturesFinder";
import PriorityBadge from "./ui/PriorityBadge";
import ExportManagerStyled from "./ExportManagerStyled";
import InsightPanel from "./InsightPanel";
import TopologyPanel from "./TopologyPanel";
import apiClient from '../utils/apiClient';
import { secureStorage } from '../utils/secureStorage'; // 🔒 SÉCURITÉ - Stockage chiffré
import { getTestsCount } from '../utils/testUtils';
import TagsList from "./ui/TagsList";
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { saveSessionData, loadSessionData } from "../utils/sessionStorageClient";
import { savePendingNodes, loadPendingNodes, forceSyncPendingNodes } from "../utils/pendingNodesSync";

// Debug tools en développement
if (process.env.NODE_ENV === 'development') {
  import("../utils/debugPendingNodesSync").catch(console.warn);
}
import Modal from "./Modal";
import LabSectionModal from "./LabSectionModal";
import ImpactRadarModal from "./ImpactRadarModal";
import TestGenerationModal from "./TestGenerationModal";
import Card from "../components/ui/card";
import Button from "../components/ui/button";
import Input from "../components/ui/input";
import Select from "../components/ui/select";
import Spinner from "../components/ui/spinner";
import NodeDetailsPanel from "./NodeDetailsPanel";
import PremiumAccordion from './ui/PremiumAccordion';
import DMBTSectionWrapper from './ui/DMBTSectionWrapper';
import GraphView from "./graphView";
import GraphSection from "./GraphSection";
import TextToNodeCreator from "./TextToNodeCreator";
import Header from "./ui/Header";
import BackToTop from "./ui/BackToTop";
import TestFlowVisualizer from "./ui/TestFlowVisualizer.js";
import SpecificationGenerator from "./SpecificationGenerator";
import DMBTGuide from './DMBTGuide';
import DMBTTrigger from './ui/DMBTTrigger';
import Playbooks from './Playbooks';
import PlaybooksTrigger from './ui/PlaybooksTrigger';
import { Tab, TabGroup } from "./ui/tabs.js";
import TemplateLibrary, { nodeTemplates, projectExamples } from "./TemplateLibrary";
import ReactFlowRenderer from "./ui/ReactFlowRenderer";
// 📱 Import pour l'interface mobile simplifiée
import MobileLabViewSimple from "./MobileLabViewSimple";
import { useIsMobile, useBreakpoint } from "../hooks/useIsMobile";
import {
  parseMarkdownTable,
  extractAllGherkinScenarios,
  extractAllTestScenarios,
  extractGherkinSteps,
  extractAcceptanceSteps,
  extractStepsByType,
  aggregateAllTestsToFlow,
  aggregateAllTestScenariosToFlow
} from "../utils/testUtils";
import {
  FaListAlt,
  FaExclamationTriangle,
  FaCheckCircle,
  FaLightbulb,
  FaTasks,
  FaUserShield,
} from "react-icons/fa";
import MBTGraph from "./MBTGraph.js";
import GraphBootstrapFromCode from "./GraphFromCode";
import { FaCircleInfo } from 'react-icons/fa6';
import SectionAnalysis from './SectionAnalysis.js';
import UserJourneyGuide from './ui/UserJourneyGuide';
import ContextualGuide from './ui/ContextualGuide';
import NodementedFunnelModalSimple from './ui/NodementedFunnelModalSimple';
import NodementedTriggerBalanced from './ui/NodementedTriggerBalanced.js';
import LaboratoryTrigger from './ui/LaboratoryTrigger.js';
import CGUModal from './CGUModal';
import { useCGUStatus } from '../hooks/useCGUStatus';
import CGU from './CGU';
import LabNavigation from './LabNavigation';
import PersonaGuide from './PersonaGuide';
import SafeChangeFlow from './SafeChangeFlow';
import { isFeatureEnabled, FEATURES } from '../config/featureFlags';
import './LabRefactored.css';
import './LabHeader.css';

// === SECURE LOGGING HELPER ===
const isDevelopment = process.env.NODE_ENV === 'development';
const debugLog = (message, data = null) => {
  if (isDevelopment) {
    if (data) {
      console.log(message, data);
    } else {
      console.log(message);
    }
  }
};

// TODO ajouter des tutoriaux vidéos pour chaque section
// TODO Trial gratuit réactivable... une fois par user normalement, on a ce qu'il faut dans le backend, a checker
//TODO génération de noeud quelque soit le mode, normalement le llm doit pouvoir générer des tests à partir de n'importe quel noeud décrit et les ajouter directement au graphe, ça n'a pas l'air d'être le cas aujourd'hui
//TODO ajout d'un champ feature dans les noeuds pour avoir un affichage qui regroupe les nodes par features teams / produits
//TODO tutoriel sur tout le concept a revoir, les premiers tests users ne sont pas top, il faut mieux expliquer le concept global,
// TODO le workflow, les bénéfices, le lien avec la QA, les tests, la doc, etc.
//TODO le"lab": passer d'une longue page avec scroll infini à une interface avec sections/pages accessibles via un menu ou des onglets
//TODO biling page pour tous les types de users. avec vraies données et pas des mocks
//TODO Forget password backend et frontend
//TODO ajouter des images aux noeuds ? pour les captures d'écran des UI par exemple et enrichir les métadonnées et analyses
/*
 Option C (Admin‑initiated reset, sans mail)

Admin génère un token unique (affiché EN CLAIR UNE SEULE FOIS).
Token stocké haché (bcrypt) en base dans user.recoveryTokens, expiry court (ex: 30 min), used flag.
User utilise token + nouvel mot de passe via un endpoint public ; token validated, marked used, password changé, sessions invalidées.
Actions auditées (admin generate + user use) dans un log sécurisé.
TODO (prioritaire, sans régressions)

Backend — modèle
Ajouter user.recoveryTokens: [{ codeHash, createdAt, expiresAt, used, createdBy }]
Ajouter user.forcePasswordChange: Boolean
Backend — routes
POST /api/admin/user/:id/reset-generate
Auth admin strict, interdit self-reset
Génère plainToken (crypto), stocke hash, set forcePasswordChange=true
Append audit (tmp/admin-reset-audit.jsonl or Mongo)
Retourne plainToken EN CLAIR (une seule réponse)
POST /api/auth/recovery/use
Public, body { email, token, newPassword }
Vérifie hash (bcrypt), expiry, marque used, met à jour password, clear auth cookie, audit
Backend — sécurité & hardening
Autorisation admin (requireAuth + role check)
Rate limiting on both endpoints
Stockage haché avec bcrypt (salt >= 10)
Short expiry (15–60 min), limit active tokens per user
Audit structured (adminId, targetUserId, ip, ua, timestamps)
Optionnel: MFA requirement for admin action
Backend — sessions
Invalidate user sessions on generate/use (clearCookie + increment tokenVersion if present)
If session store exists, remove session entries for user
Frontend — admin UX (optionnel now)
Small admin action to call /api/admin/user/:id/reset-generate and show token once (copy button + warning)
Frontend — user recovery flow
Add "Use recovery token" form on login: submit email, token, newPassword → POST /api/auth/recovery/use
On success redirect to login
Tests & QA
Unit/integration: generate → verify DB hash saved, audit entry; use → verify password changed, token marked used, sessions cleared
E2E: admin generates token → user uses token to reset
Docs & ops
Document admin procedure + warning to transmit token securely
Add retention policy for audit logs and recovery token cleanup job (cron)
Rollout plan (safe, no regressions)
Implement model changes with migration (add fields default)
Add routes behind admin auth
Add comprehensive tests
Deploy to staging, run E2E, then prod
Optional improvements (later)
Migrate audit JSONL → Mongo collection with RBAC
Admin UI enhancements (copy once, QR, ephemeral display)
Replace token-without-mail by temporary link / email when ready
*/
//TODO specs generator doit fournir des specs en gherkin et en format json pour que je puisse les stocker dans la base de données et pour créer directement des noeuds avec les tests associés.
//TODO GÉNÉRATION DE NŒUDS DEPUIS SPÉCIFICATIONS - Feature en réflexion
//   Questions à résoudre avant implémentation :
//   1. WORKFLOW : Automatique lors de génération vs bouton manuel ?
//   2. TYPES : Feature vs Specification vs Test - quelle granularité ?
//   3. RELATIONS : Comment gérer les liens automatiquement ?
//   4. UX : Éviter confusion entre Export (JSON) et Génération (nœuds)
//   5. VALIDATION : Preview avant création ? Édition possible ?
//   Voir code commenté dans handleCreateNodesFromSpecification
//TODO openAI pour gpt5
//TODO supp des users ko ? on veut sup ou block ?
//TODO test a design where the graph is the main element and other tools are visible after clicking buttons in the header (modals or side panel or other)
//TODO dans le cartouche free plan, vraiment brancher les stats de conso et idem dans les dashboard surtout super admin pour avoir la conso des users
//TODO should specifications and tests be specific nodes or metadata of nodes? I'm afraid it might be too visually complex for users - too many types of nodes.
/*TODO
Doctrine Formelle (inspirée BDD, mais augmentée)

On pourrait formaliser un langage type Nodemented Spec Language (NSL), basé sur Gherkin mais enrichi de métadonnées qualité et graphe.

Exemple :

Feature: Payment API

  Quality:
    Coverage: 80%
    Criticality: High
    RequiredTests: LoadTest, SecurityTest

  Scenario: Successful payment
    Given a valid user
    When they submit a payment
    Then the transaction is marked as "completed"
    And the payment API node has a "validated_by" relation to "Payment Test Suite"


Différence vs Gherkin classique :

Chaque scénario inclut métadonnées qualité (coverage, criticality, required tests).

Les scénarios sont automatiquement liés aux nœuds dans le graphe.
*/
//TODO update header to reduce the size of "icons" and add an icon for spec generator
//TODO improve spec generator and display of generated specs export etc, database storage?
// TODO: Revisit super-admin dashboard & analytics wiring
// - Ensure notifications are fetched from the real API (no mocks)
// - Add alias or fix frontend call for restore-context / exit-impersonation
// - Consolidate dashboard components (prefer SuperAdminDashboardEnhanced)
//TODO spec/code generator linked to nodes (no code tool :))
// TODO add multiple pages or block the workflow to avoid losing users, there's too much freedom and therefore too much confusion to understand the global concept.
// TODO ajouter un bouton sur le dashboard pour importer un projet existant.
// TODO analyser en fonction de personna QA dev Po, et peut-être définir des parcours types avec un affichage type.
//TODO dans l'analyse des noeuds il faut réussir a hiérarchiser les infos et prévenir le user si il y a trop d'infos à traiter / inutiles
/*
cas user seul qui veut rejoindre une organisation et migrer (fusionner) ses noeuds et relationsavec ceux de l'organisation.

et cas du user freelance qui reset toutes ses infos noeuds tets etc
comment on gère ça? */
//TODO affichage des tests générés dégeu... a moderniser dans "Réponse de l'IA Pour: <nom du noeud>" 
//TODO revoir la partie Découvrez Nodemented, sur la page login, avec de vraies captures d'écran et infos 
// TODO COMMENTAIRES COLLABORATIFS

// ========================================================================================
// 🎫 TODO SYSTÈME DE TRACKING LICENCES - IMPLÉMENTATION COMPLÈTE
// ========================================================================================
//TODO 1. MIDDLEWARE DE COMPTAGE API CALLS
//   - Créer middleware countApiCall() dans backend/middleware/
//   - Intercepter toutes les routes API et incrémenter usage.apiCalls
//   - Bloquer si limite atteinte (avec message d'upgrade)
//   - Exceptions pour super-admins et routes auth

//TODO 2. TRACKING DES NŒUDS EN TEMPS RÉEL  
//   - Hook dans routes de création de nœuds (/api/nodes POST)
//   - Incrémenter usage.nodes automatiquement
//   - Validation des limites avant création
//   - Rollback si échec pour cohérence données

//TODO 3. SYSTÈME DE SURVEILLANCE STOCKAGE
//   - Calculer taille des fichiers uploadés
//   - Tracking des embeddings et métadonnées
//   - Alerte avant dépassement limite
//   - Cleanup automatique des données temporaires

//TODO 4. AUTO-UPGRADE ET GESTION LIMITES
//   - Notification intelligente à 80% et 95% de limite
//   - Proposition d'upgrade automatique
//   - Blocage gracieux avec options de mise à niveau
//   - Interface de gestion des quotas

//TODO 5. REPORTING ET ANALYTICS USAGE
//   - Dashboard détaillé d'utilisation par user/organization
//   - Historique de consommation (graphiques)
//   - Prédictions de dépassement basées sur usage
//   - Export des métriques pour facturation

//TODO 6. INTÉGRATION STRIPE WEBHOOKS
//   - Gestion événements payment_succeeded
//   - Mise à jour automatique des limites
//   - Synchronisation état licence <-> paiement
//   - Gestion des échecs et relances

//TODO 7. OPTIMISATIONS PERFORMANCE
//   - Cache Redis pour éviter requêtes répétées
//   - Agrégation usage en batch (toutes les heures)
//   - Indexation MongoDB pour requêtes rapides
//   - Monitoring des performances de tracking

// ========================================================================================
// 💳 TODO SYSTÈME DE PAIEMENT STRIPE - INTÉGRATION COMPLÈTE
// ========================================================================================
//TODO 1. CONFIGURATION STRIPE BACKEND
//   - Installation stripe npm package
//   - Configuration clés API (test/prod) dans .env
//   - Création des Products et Prices dans Stripe Dashboard
//   - Middleware de sécurité pour webhook signatures

//TODO 2. ROUTES API PAIEMENT
//   - POST /api/stripe/create-checkout-session (upgrade plans)
//   - POST /api/stripe/create-portal-session (gestion abonnements)
//   - POST /api/stripe/webhook (événements Stripe)
//   - GET /api/stripe/plans (récupération prix dynamiques)

//TODO 3. FRONTEND INTÉGRATION STRIPE
//   - Installation @stripe/stripe-js
//   - Composant StripeCheckout avec formulaire sécurisé
//   - Page de succès/échec après paiement
//   - Redirection vers portal client Stripe

//TODO 4. GESTION ABONNEMENTS ET CYCLES
//   - Webhook payment_intent.succeeded
//   - Webhook invoice.payment_succeeded  
//   - Webhook customer.subscription.updated/deleted
//   - Gestion des échecs de paiement et relances

//TODO 5. FACTURATION ET TAXES
//   - Configuration TVA européenne dans Stripe
//   - Génération automatique factures PDF
//   - Export comptable des transactions
//   - Gestion des remboursements et crédits

// ========================================================================================
// 📊 TODO INTERFACE SUPER ADMIN FINANCIÈRE - BUSINESS INTELLIGENCE
// ========================================================================================
//TODO 1. DASHBOARD REVENUS ET ABONNEMENTS
//   - Graphiques revenus mensuels/annuels (Chart.js)
//   - Métriques MRR (Monthly Recurring Revenue)
//   - Churn rate et retention par plan
//   - Prévisions de revenus basées sur tendances

//TODO 2. GESTION DES COÛTS OPÉRATIONNELS
//   - Tracking coûts OpenAI API (tokens utilisés × tarif)
//   - Monitoring coûts Neo4j (requêtes × temps d'exécution)
//   - Calcul coûts infrastructure (serveurs, stockage)
//   - Dashboard profit/perte par utilisateur

//TODO 3. ANALYTICS UTILISATEURS PAYANTS
//   - Liste des abonnements actifs avec détails
//   - Historique des paiements et échecs
//   - Segmentation par plan (Starter/Pro/Enterprise)
//   - Analyse de l'utilisation vs limites par user

//TODO 4. RAPPORTS FINANCIERS AUTOMATISÉS
//   - Export mensuel des revenus (CSV/PDF)
//   - Rapport de réconciliation Stripe
//   - Calcul automatique des marges par plan
//   - Alertes sur métriques critiques (churn élevé, etc.)

//TODO 5. OPTIMISATION PRICING ET PLANS
//   - A/B testing sur les prix
//   - Analyse élasticité prix-demande
//   - Recommandations d'upgrade automatiques
//   - Modélisation impact changements tarifaires

//TODO 6. MONITORING TEMPS RÉEL BUSINESS
//   - Dashboard live des paiements du jour
//   - Alertes échecs de paiement critiques
//   - Suivi quotidien des nouvelles inscriptions
//   - Métriques de conversion trial → payant

//TODO 7. INTÉGRATION COMPTABILITÉ
//   - Export vers logiciels comptables (QuickBooks, etc.)
//   - Synchronisation avec banques (API bancaires)
//   - Gestion multi-devises si expansion internationale
//   - Automatisation déclarations fiscales
// ========================================================================================
// V1 (local, non nominatif)
// - Ajouter un panneau de commentaire local (non nominatif) visible uniquement par l'utilisateur connecté (scrum master de la session)
// - Les commentaires ne sont pas synchronisés entre utilisateurs, pas de base de données, juste localStorage ou state
// - Prévoir un export de rapport de session (PDF) qui inclut :
//     - Les nodes créés
//     - Les relations créées
//     - Les tests créés
//     - Les commentaires saisis pendant la session

// V2 (collaboratif, nominatif, multi-utilisateur)
// - Stockage des commentaires côté backend (API REST ou WebSocket)
// - Chaque commentaire est associé à :
//     - un auteur (user connecté)
//     - un timestamp
//     - une session, une équipe ou un projet (filtrage possible)
//     - éventuellement un contexte (ex : lié à un node, une relation, un test, ou global à la session)
// - Affichage en temps réel pour tous les membres de l'équipe (WebSocket ou polling)
// - Possibilité de supprimer/éditer ses propres commentaires, droits d'admin pour le scrum master
// - Historique des commentaires par session ou par projet
// - Notifications en temps réel lors de nouveaux commentaires
// - Export PDF enrichi : inclure auteur, date, contexte, etc.
// - (optionnel) Mentions, réactions, threads/réponses, etc.

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
// =====================================================================
//TODO PROTECTION CONTRE PROMPT INJECTION !!! 
//TODO générateur de critères d'acceptation en partant de la description du noeud/feature/ticket jira ?
//TODO générateur de tests en partant de la description du noeud/feature/ticket jira en langage naturelle ?
//TODO ajouter un bouton pour exporter le graphe en JSON ou en fichier .graphml
//TODO ajouter l'update des tests dans le graph globale et que la modification soit enregistrée
// dans la base de données, j'ai un soucis de duplication des tests quand on update
// dans le graphe globale et les nouveaux steps ne sont pas enregistrés dans le
// scénarios sélectionné, on les voit en local par contre.

//TODO affichage des noeuds par niveaux, Afficher les noeuds parents puis les enfants

//TODO étudier l'idée d'analyser les nœuds en cours de modélisation par l'IA pour enrichir automatiquement les métadonnées, suggérer des relations et valider la cohérence avant intégration à la base de connaissance

// =====================================================================
// ================ COMPOSANT PRINCIPAL NODEMANAGER ====================
// =====================================================================
const FeatureManager = ({ onBackToDashboard, initialView, user, onLogout, persona: initialPersona, openSafeChangeFlow: initialOpenSCF, onSafeChangeFlowClosed }) => {
  // Hook de traduction
  const { t, i18n } = useTranslation();
  
  // Hook de localisation pour détecter la navigation depuis SmartTools
  const location = useLocation();

  // 📱 Hooks pour la détection mobile et responsive
  const isMobile = useIsMobile();
  const breakpoint = useBreakpoint();

  // =================================================================
  // =================================================================
  // ================= DÉFINITION DES ÉTATS DU COMPOSANT =================
  // =====================================================================
  const [nodeName, setNodeName] = useState("");
  const [graphRefreshFn, setGraphRefreshFn] = useState(null);
  const [nodeDescription, setNodeDescription] = useState("");
  const [nodeType, setNodeType] = useState("");
  const [technology, setTechnology] = useState("");
  const [featureTeam, setFeatureTeam] = useState("");  // 👈 NOUVEAU : Feature team state
  const [status, setStatus] = useState("");
  const [nodes, setNodes] = useState([]);
  const [metadata, setMetadata] = useState("");
  const [endpointSchema, setEndpointSchema] = useState("");
  const [selectedNode, setSelectedNode] = useState(null);
  const [relationships, setRelationships] = useState([]);
  const [relationshipType, setRelationshipType] = useState("");
  const [targetNode, setTargetNode] = useState("");
  const [targetNodeSearch, setTargetNodeSearch] = useState("");
  const [allRelationships, setAllRelationships] = useState([]);
  const [testScenarios, setTestScenarios] = useState([]);
  const [selectedTestScenario, setSelectedTestScenario] = useState(null);
  const [nodeTests, setNodeTests] = useState([]);
  const [analysisResult, setSchemaAnalysis] = useState("");
  const [llmResponse, setGeneratedTest] = useState("");
  const [nodeSearch, setNodeSearch] = useState("");
  const filteredNodes = nodes.filter(node =>
    node.name.toLowerCase().includes(nodeSearch.toLowerCase())
  );
  const [showTemplateLibrary, setShowTemplateLibrary] = useState(false);
  // À ajouter aux états du composant
  const [expandedRelation, setExpandedRelation] = useState(null);
  // Ajouter cette ligne avec les autres déclarations useState au début du composant
  const [customRelationshipType, setCustomRelationshipType] = useState("");
  const [loading, setLoading] = useState(false);
  const [llmScenarios, setLlmScenarios] = useState([]);
  const [showStoredPreview, setShowStoredPreview] = useState(false);
  
  // État pour auto-charger les tests quand la modal s'ouvre via NodeDetailsPanel
  const [autoLoadTests, setAutoLoadTests] = useState(false);

  // 📜 États pour la gestion des CGU
  const { cguStatus, acceptCGU, declineCGU, isAccepted, isLoading } = useCGUStatus();
  const [showCGUModal, setShowCGUModal] = useState(false);
  const [showCGUPage, setShowCGUPage] = useState(false);
  const [showTestGenerationModal, setShowTestGenerationModal] = useState(false);
  const userEmail = user?.email || user?.username || 'unknown';
  
  // 🔌 Integration states for Lab JIRA section
  const { integrations, refreshIntegrations } = useIntegrations();
  const [showIntegrationSetup, setShowIntegrationSetup] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);

  const clearStoredLlmScenarios = () => {
    setLlmScenarios([]);
    saveSessionData("llmScenarios", []);
  };

  // Ajouter ces états dans le composant FeatureManager
  const [currentPage, setCurrentPage] = useState(1);
  const [nodesPerPage, setNodesPerPage] = useState(10); // 9 nœuds par page (3x3)

  // États pour le filtrage et la pagination des relations
  const [relationshipSearch, setRelationshipSearch] = useState("");
  const [relationshipTypeFilter, setRelationshipTypeFilter] = useState("");
  const [currentRelationshipPage, setCurrentRelationshipPage] = useState(1);
  const [relationsPerPage] = useState(5);

  // États pour la navigation et les guides utilisateur
  const [currentSection, setCurrentSection] = useState('section-graph');
  const [showUserJourney, setShowUserJourney] = useState(true);
  const [showContextualGuide, setShowContextualGuide] = useState(true);

  // État pour le funnel Nodemented
  const [showNodementedFunnel, setShowNodementedFunnel] = useState(false);

  // État pour le créateur de tests manuels
  const [showManualTestCreator, setShowManualTestCreator] = useState(false);
  const [editingManualTest, setEditingManualTest] = useState(null);

  // État pour le générateur de spécifications IA
  const [showSpecificationGenerator, setShowSpecificationGenerator] = useState(false);
  const [generatedSpecifications, setGeneratedSpecifications] = useState([]);

  // État pour le guide DMBT
  const [showDMBTGuide, setShowDMBTGuide] = useState(false);

  // État pour les Playbooks
  const [showPlaybooks, setShowPlaybooks] = useState(false);

  // ── Insight Engine ────────────────────────────────────────────────────────
  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [insightsVisible, setInsightsVisible] = useState(true);
  const insightsFetchedRef = useRef(false);

  // ── Post-import CTA banner ────────────────────────────────────────────────
  const [postImportBanner, setPostImportBanner] = useState(null); // { count, type }

  // ref pour scroller vers la navigation du lab
  const labNavRef = useRef(null);

  // ===== ÉTATS POUR LA GESTION DES SECTIONS LAB (RESTAURÉ) =====
  const [activeSection, setActiveSection] = useState(null);

  // ===== PERSONA GUIDE STATE =====
  const personaGuideRef = useRef(null);
  const [activePersona, setActivePersona] = useState(() => {
    return initialPersona || localStorage.getItem('mc_persona') || null;
  });
  // Only show PersonaGuide when an explicit persona was passed (e.g. from MissionControl action cards).
  // "Enter the lab" navigates without a persona so we land on the graph directly.
  const [showPersonaGuide, setShowPersonaGuide] = useState(() => {
    return isFeatureEnabled(FEATURES.MISSION_CONTROL) && !!initialPersona;
  });
  // Derived: is PersonaGuide currently replacing old guides?
  const isPersonaGuideActive = showPersonaGuide && activePersona && isFeatureEnabled(FEATURES.MISSION_CONTROL);

  // SafeChangeFlow modal state
  const [showSafeChangeFlow, setShowSafeChangeFlow] = useState(!!initialOpenSCF);

  // Auto-open SafeChangeFlow when triggered from Dashboard/MissionControl
  useEffect(() => {
    if (initialOpenSCF) setShowSafeChangeFlow(true);
  }, [initialOpenSCF]);

  // Auto-scroll vers PersonaGuide quand on arrive avec un persona actif
  useEffect(() => {
    if (isPersonaGuideActive && personaGuideRef.current) {
      setTimeout(() => {
        personaGuideRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 400);
    }
  }, [isPersonaGuideActive]);

  // Fonction pour ouvrir une section en modal
  const openLabSection = (sectionId, autoLoad = false) => {
    setActiveSection(sectionId);
    
    // Si c'est la section tests et autoLoad est true, activer l'auto-chargement
    if (sectionId === 'tests' && autoLoad) {
      setAutoLoadTests(true);
    }
  };

  // Fonction pour fermer la modal active
  const closeLabSection = () => {
    setActiveSection(null);
    setAutoLoadTests(false); // Réinitialiser l'auto-chargement
  }


  // Ref pour la fonction de refresh des spécifications
  const specificationsRefreshRef = useRef(null);

  const addPendingNode = (node) => {
    setPendingNodes((prev) => [
      { ...node, id: Date.now(), createdAt: new Date().toISOString() },
      ...prev,
    ]);
  };

  // Cette fonction charge un modèle de nœud dans le formulaire
  const handleSelectTemplate = (template) => {
    setNodeName(template.name);
    setNodeDescription(template.description);
    setNodeType(template.type);
    setTechnology(template.technology);
    setStatus(template.status);
    setMetadata(JSON.stringify(template.metadata || {}, null, 2));
    setEndpointSchema(JSON.stringify(template.endpointSchema || {}, null, 2));

    // Fermer la bibliothèque
    setShowTemplateLibrary(false);

    // Optionnel: faire défiler jusqu'au formulaire
    const targetElement = document.getElementById("section-create-node");
    if (targetElement) {
      targetElement.scrollIntoView({ behavior: "smooth" });
    }
  };

  // Cette fonction importe un exemple complet (nœuds + relations)
  const handleImportExample = async (example) => {
    if (window.confirm(t('featureManager.importConfirm', {
      exampleName: Object.keys(projectExamples).find(key => projectExamples[key] === example),
      nodeCount: example.nodes.length,
      relationCount: example.relationships.length
    }))) {
      setLoading(true);
      try {
        const response = await apiClient.post(`/import-example`, example);

        // Mettre à jour la vue
        fetchNodes();

        // Message de succès
        toast.success(t('featureManager.importSuccess', {
          nodesAdded: response.data.stats.nodes.added,
          relationsAdded: response.data.stats.relationships.added
        }));

        // Fermer la bibliothèque
        setShowTemplateLibrary(false);
      } catch (error) {
        toast.error(t('featureManager.importError', { message: error.response?.data?.message || error.message }));
      } finally {
        setLoading(false);
      }
    }
  };

  // ===== FONCTIONS POUR LES MODALES SPÉCIALISÉES =====

  // Import Code handlers
  const handleImportCode = async (data) => {
    setLoading(true);
    try {
      // TODO: Implémenter l'import de code
      console.log('Import code:', data);
      toast.success('Import de code implémenté avec succès !');
      setPostImportBanner({ count: data?.nodes?.length || 1, type: 'code' });
    } catch (error) {
      console.error('Import error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleImportFile = async (data) => {
    setLoading(true);
    try {
      // TODO: Implémenter l'import de fichier
      console.log('Import file:', data);
      toast.success('Import de fichier implémenté avec succès !');
      setPostImportBanner({ count: data?.nodes?.length || 1, type: 'file' });
    } catch (error) {
      console.error('Import file error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleImportRepository = async (data) => {
    setLoading(true);
    try {
      // TODO: Implémenter l'import de dépôt
      console.log('Import repository:', data);
      toast.success('Import de dépôt implémenté avec succès !');
      setPostImportBanner({ count: data?.nodes?.length || 1, type: 'repository' });
    } catch (error) {
      console.error('Import repository error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Quick Create handlers
  const handleQuickCreateNode = async (nodeData) => {
    setLoading(true);
    try {
      const response = await apiClient.post(`/nodes`, nodeData);
      setNodes(prev => {
        const updated = [response.data, ...prev];
        return updated;
      });
      toast.success('Nœud créé avec succès !');
      return response.data;
    } catch (error) {
      console.error('Quick create node error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleQuickCreateRelation = async (relationData) => {
    setLoading(true);
    try {
      const response = await apiClient.post(`/relationships`, relationData);
      setRelationships(prev => [response.data, ...prev]);
      toast.success('Relation créée avec succès !');
      return response.data;
    } catch (error) {
      console.error('Quick create relation error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleQuickCreateTest = async (testData) => {
    setLoading(true);
    try {
      // TODO: Implémenter la création rapide de test
      console.log('Quick create test:', testData);
      toast.success('Test créé avec succès !');
      return testData;
    } catch (error) {
      console.error('Quick create test error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleQuickCreateTemplate = async (templateData) => {
    setLoading(true);
    try {
      // TODO: Implémenter la création de template
      console.log('Quick create template:', templateData);
      toast.success('Template créé avec succès !');
      return templateData;
    } catch (error) {
      console.error('Quick create template error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Test handlers
  const handleRunTest = async (testData) => {
    setLoading(true);
    try {
      // TODO: Implémenter l'exécution de test
      console.log('Run test:', testData);
      toast.success('Test exécuté avec succès !');
      return { status: 'passed', results: [] };
    } catch (error) {
      console.error('Run test error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTest = async (testData) => {
    setLoading(true);
    try {
      // TODO: Implémenter la création de test
      console.log('Create test:', testData);
      toast.success('Test créé avec succès !');
      return testData;
    } catch (error) {
      console.error('Create test error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTest = async (testId) => {
    setLoading(true);
    try {
      // TODO: Implémenter la suppression de test
      console.log('Delete test:', testId);
      toast.success('Test supprimé avec succès !');
    } catch (error) {
      console.error('Delete test error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Vector Search handlers
  const handleVectorSearch = async (searchData) => {
    setLoading(true);
    try {
      // TODO: Implémenter la recherche vectorielle
      console.log('Vector search:', searchData);
      toast.success('Recherche vectorielle exécutée !');
      return { results: [], total: 0 };
    } catch (error) {
      console.error('Vector search error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Analysis handlers
  const handleAnalysis = async (analysisData) => {
    setLoading(true);
    try {
      // TODO: Implémenter l'analyse
      console.log('Analysis:', analysisData);
      toast.success('Analyse exécutée avec succès !');
      return { results: [], insights: [] };
    } catch (error) {
      console.error('Analysis error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Specification handlers
  const handleGenerateSpecification = async (specData) => {
    setLoading(true);
    try {
      // TODO: Implémenter la génération de spécification
      console.log('Generate specification:', specData);
      toast.success('Spécification générée avec succès !');
      return { content: '', type: specData.type };
    } catch (error) {
      console.error('Generate specification error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Filtrer les relations en fonction de la recherche et du type
  const filteredRelationships = useMemo(() => {
    return relationships.filter((rel) => {
      // Filtrer par type
      if (
        relationshipTypeFilter &&
        rel.relationshipType !== relationshipTypeFilter
      ) {
        return false;
      }

      // Filtrer par recherche textuelle
      if (relationshipSearch) {
        const searchLower = relationshipSearch.toLowerCase();
        return (
          rel.nodeName1.toLowerCase().includes(searchLower) ||
          rel.nodeName2.toLowerCase().includes(searchLower) ||
          rel.relationshipType.toLowerCase().includes(searchLower)
        );
      }

      return true;
    });
  }, [relationships, relationshipTypeFilter, relationshipSearch]);

  // Relations à afficher sur la page actuelle
  const indexOfLastRelationship = currentRelationshipPage * relationsPerPage;
  const indexOfFirstRelationship = indexOfLastRelationship - relationsPerPage;
  const currentRelationships = filteredRelationships.slice(
    indexOfFirstRelationship,
    indexOfLastRelationship
  );
  // Calculer les nœuds à afficher pour la page actuelle

  const indexOfLastNode = currentPage * nodesPerPage;
  const indexOfFirstNode = indexOfLastNode - nodesPerPage;
  const currentNodes = filteredNodes.slice(indexOfFirstNode, indexOfLastNode);

  const handleGraphValidated = async (graph) => {
    // Envoie le graphe validé au backend
    await apiClient.post('/import-graph', graph);
    // Optionnel : afficher un toast, rafraîchir la vue, etc.
    toast.success(t('featureManager.graphImported'));
  };

  // Fonction pour changer de page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  //TODO afficher le graph sur le coté gauche ( ou droit ) et les metadonnées du noeud sélectionné sur l'autre partie de l'écran exemple pris sur les steps functions de AWS
  const openStoredPreview = () => setShowStoredPreview(true);
  const closeStoredPreview = () => setShowStoredPreview(false);
  // --- Ajout : Charger les scénarios LLM depuis le localStorage au montage ---
  useEffect(() => {
    // Restaure les données depuis localStorage uniquement au montage
    try {
      // 🚫 TEMPORAIREMENT DÉSACTIVÉ - Sélection automatique depuis localStorage
      /*
      const selectedNodeLocal = localStorage.getItem("selectedNode");
      if (selectedNodeLocal) {
        try {
          setSelectedNode(JSON.parse(selectedNodeLocal));
        } catch (e) {
          console.warn('Could not parse selectedNode from localStorage', e);
        }
      }
      */

      const pendingNodesLocal = localStorage.getItem("pendingNodes");
      if (pendingNodesLocal) {
        try {
          let restored = JSON.parse(pendingNodesLocal);
          if (Array.isArray(restored) && restored.length > 0) {
            // Injecter les steps Gherkin si manquants
            const { extractGherkinSteps } = require("../utils/testUtils");
            const hydrated = restored.map((test) => {
              try {
                if (!Array.isArray(test.steps) || test.steps.length === 0) {
                  const steps = extractGherkinSteps(test.description || test.gherkin || "");
                  return { ...test, steps };
                }
                return test;
              } catch (e) {
                // Si quelque chose casse pour un test, on renvoie l'original
                return test;
              }
            });
            setPendingNodes(hydrated);
          }
        } catch (e) {
          console.warn('Could not parse pendingNodes from localStorage', e);
        }
      }
    } catch (e) {
      console.error('Error restoring local data for FeatureManager:', e);
    }
  }, []);

  const navigateTo = (sectionId) => {
    const el = document.getElementById(sectionId);
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
      setCurrentSection(sectionId);
    }
  };

  // Fonction pour naviguer vers une section spécifique
  const handleNavigateToSection = (sectionId) => {
    navigateTo(sectionId);
  };

  // Fonction pour gérer les actions contextuelles
  const handleContextualAction = (action) => {
    switch (action) {
      case 'create-first-node':
        navigateTo('section-quick-create');
        break;
      case 'quick-create':
        navigateTo('section-quick-create');
        break;
      case 'import-code':
        navigateTo('section-import-code');
        break;
      case 'analyze':
        if (selectedNode) {
          navigateTo('section-analysis');
        }
        break;
      case 'use-template':
        setShowTemplateLibrary(true);
        break;
      case 'from-code':
        navigateTo('section-import-code');
        break;
      default:
        console.log('Unrecognized action'); // Important error
    }
  };

  // Détecter la section visible
  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        'section-graph',
        'section-quick-create',
        'section-create-node',
        'section-nodes-list',
        'section-create-relation',
        'section-relations-list',
        'section-analysis',
        'section-tests-global',
        'section-generate-tests',
        'section-vector-search',
        'section-import-code'
      ];

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top >= 0 && rect.top <= window.innerHeight / 2) {
            setCurrentSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);

    // Listener pour l'événement addToModeling
    const handleAddToModeling = (event) => {
      // Ajoute chaque test en gardant toutes ses propriétés, steps, scenario, etc.
      const { tests } = event.detail;
      if (tests && Array.isArray(tests)) {
        let added = 0;
        tests.forEach(test => {
          const testName = test.name || test.title || `Test #${Date.now()}`;
          if (pendingNodes.some(n => n.name === testName || n.title === testName)) return;
          
          // 🔧 STANDARDISATION COMPLÈTE: S'assurer que chaque test a un targetNode robuste
          const targetNode = test.targetNode || 
                            test.nodeName || 
                            test.associatedNodes?.[0] || 
                            test.nodesInvolved?.[0] ||
                            selectedNode?.name || // 👈 NOUVEAU: Utiliser le nœud sélectionné comme fallback
                            extractNodeNameFromTitle(test.title || test.name) ||
                            extractNodeNameFromDescription(test.description);

          // On copie toutes les propriétés du test, et on force type/nodeType à 'Test'
          const pendingTest = {
            ...test,
            id: Date.now() + Math.random(),
            name: testName,
            type: 'Test',
            nodeType: 'Test',
            targetNode: targetNode, // 👈 CRUCIAL: Toujours présent
            nodeName: targetNode,   // 👈 Alias pour compatibilité
            createdAt: new Date().toISOString(),
            // Pour compatibilité, on garde steps/scenario/gherkin si présents
            steps: Array.isArray(test.steps) ? test.steps : [],
            scenario: test.scenario || test.testScenario || '',
            scenarios: test.scenarios || [], // Utiliser la propriété scenarios transmise depuis ImpactRadarModal
            gherkin: Array.isArray(test.steps) && test.steps.some(s => s.type && s.text) ? test.steps.map(s => `${s.type} ${s.text}`).join('\n') : '',
            relatedNode: targetNode, // 👈 NOUVEAU: Nœud auquel le test est lié
            metadata: {
              ...(test.metadata || {}),
              sourceType: "impact-radar",
              testType: test.type || "acceptance",
              priority: test.priority || "normal",
              targetNode: targetNode, // 👈 CRUCIAL: Aussi dans les métadonnées
              scenario: test.scenario || test.testScenario || '',
              scenarios: test.scenarios || [], // S'assurer que scenarios est aussi dans metadata
              steps: Array.isArray(test.steps) ? test.steps : [],
              expectedOutcome: test.expectedOutcome || "",
              associatedNodes: test.associatedNodes || test.nodesInvolved || [],
              relatedNode: targetNode, // 👈 NOUVEAU: Nœud auquel le test est lié
              generatedAt: new Date().toISOString(),
            },
          };
          
          // 🔍 VALIDATION: Vérifier la structure avant ajout
          const validation = validateTestStructure(pendingTest);
          if (!validation.isValid) {
            console.warn('⚠️ Test avec structure incomplète:', validation.issues, pendingTest);
            // Ajouter quand même mais avec warning
          }
          
          addPendingNode(pendingTest);
          added++;
        });
        if (added > 0) {
          toast.success(t('featureManager.testsAdded', { count: added }));
        } else {
          toast.info(t('featureManager.noTestsAdded'));
        }
      } else {
        // console.log('Aucun test valide à ajouter:', event.detail); // Log détaillé commenté
      }
    };

    window.addEventListener('addToModeling', handleAddToModeling);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('addToModeling', handleAddToModeling);
    };
  }, [addPendingNode]);

  useEffect(() => {
    if (llmResponse && typeof llmResponse === "string") {
      const scenarios = extractAllTestScenarios(llmResponse);
      setLlmScenarios(scenarios);
    }
  }, [llmResponse]);

  // --- Ajout : Sauvegarder dans le localStorage à chaque changement ---
  useEffect(() => {
    if (llmScenarios && llmScenarios.length > 0) {
      saveSessionData("llmScenarios", llmScenarios);
      if (selectedNode) {
        saveSessionData("llmScenariosNode", selectedNode);
      }
    }
  }, [llmScenarios, selectedNode]);

  useEffect(() => {
    setLoading(true);

    // Récupérer les nœuds
    apiClient.get(`/nodes`)
      .then((response) => {
        setNodes(response.data);
      })
      .catch((error) => {
        console.error("Erreur lors du chargement des nœuds :", error);
      });

    // Récupérer toutes les relations
    apiClient.get(`/relationships`)
      .then((response) => {
        setAllRelationships(response.data); // relations globales
        setRelationships(response.data); // relations visibles (pour le panneau de droite)
      })
      .catch((error) => {
        console.error("Erreur lors du chargement des relations :", error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);



  // �📜 useEffect pour la gestion des CGU
  useEffect(() => {
    if (!isLoading && !isAccepted && !showCGUModal) {
      setShowCGUModal(true);
    }
  }, [isLoading, isAccepted, showCGUModal]);

  // 📜 Handlers pour les CGU
  const handleCGUAccept = async () => {
    try {
      await acceptCGU('1.0');
      setShowCGUModal(false);
    } catch (error) {
      console.error('Erreur lors de l\'acceptation des CGU:', error);
      // Garder la modal ouverte en cas d'erreur
    }
  };

  const handleCGUDecline = async () => {
    try {
      await declineCGU();
      // 🔒 SÉCURITÉ: Rediriger vers la page de connexion avec nettoyage sécurisé
      secureStorage.removeItem('authData');
      onLogout();
    } catch (error) {
      console.error('Erreur lors du refus des CGU:', error);
      // Rediriger quand même avec nettoyage sécurisé
      secureStorage.removeItem('authData');
      onLogout();
    }
  };

  const handleShowCGUPage = () => {
    setShowCGUPage(true);
  };

  const handleCloseCGUPage = () => {
    setShowCGUPage(false);
  };

  // =====================================================================
  // ==================== FONCTIONS DE GESTION DES NŒUDS =================
  // =====================================================================


  const renderLLMResponse = (response) => {
    const lines = response.split("\n");

    const elements = [];
    let currentBlock = [];
    let insideOl = false;
    let listItems = [];

    let insideCodeBlock = false;
    let codeLines = [];

    const flushTextBlock = () => {
      if (currentBlock.length > 0) {
        elements.push(
          <p
            key={`p-${elements.length}`}
            className="mb-3 text-gray-700 leading-relaxed font-medium"
          >
            {currentBlock.join(" ")}
          </p>
        );
        currentBlock = [];
      }
    };

    const flushList = () => {
      if (insideOl && listItems.length > 0) {
        elements.push(
          <ol
            key={`ol-${elements.length}`}
            className="ml-6 list-decimal text-gray-700 leading-relaxed mb-4 space-y-2"
          >
            {listItems}
          </ol>
        );
        insideOl = false;
        listItems = [];
      }
    };

    const flushCodeBlock = () => {
      if (codeLines.length > 0) {
        const codeContent = codeLines.join("\n");
        elements.push(
          <div
            key={`code-${elements.length}`}
            className="relative bg-gradient-to-br from-gray-900 to-gray-800 text-white p-6 rounded-xl mb-6 shadow-lg border border-gray-700"
          >
            <div className="absolute top-3 right-3">
              <CopyButton textToCopy={codeContent} />
            </div>
            <div className="flex items-center gap-2 mb-3 text-gray-300">
              <div className="w-3 h-3 rounded-full bg-red-500"></div>
              <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
              <span className="ml-2 text-sm font-mono">{t('featureManager.generatedCode')}</span>
            </div>
            <pre className="overflow-x-auto whitespace-pre-wrap text-sm font-mono leading-relaxed">
              <code className="text-green-300">{codeContent}</code>
            </pre>
          </div>
        );
        codeLines = [];
      }
    };

    let i = 0;
    while (i < lines.length) {
      const line = lines[i].trim();

      // Détection des scénarios de test (Given/When/Then)
      if (/^(Given|When|Then|And|But)\s/i.test(line)) {
        flushTextBlock();
        flushList();

        const testSteps = [];
        let j = i;

        while (j < lines.length && /^(Given|When|Then|And|But)\s/i.test(lines[j].trim())) {
          const stepLine = lines[j].trim();
          const stepType = stepLine.match(/^(Given|When|Then|And|But)/i)[0];
          const stepText = stepLine.replace(/^(Given|When|Then|And|But)\s/i, '');

          const stepColor = stepType.toLowerCase() === 'given' ? 'amber' :
            stepType.toLowerCase() === 'when' ? 'blue' :
              stepType.toLowerCase() === 'then' ? 'green' : 'gray';

          testSteps.push(
            <div key={`step-${j}`} className={`flex items-start gap-3 p-3 bg-${stepColor}-50 border-l-4 border-${stepColor}-400 rounded-r-lg mb-2`}>
              <span className={`inline-block px-2 py-1 text-xs font-bold text-${stepColor}-800 bg-${stepColor}-200 rounded uppercase`}>
                {stepType}
              </span>
              <span className="text-gray-700 font-medium flex-1">{stepText}</span>
            </div>
          );
          j++;
        }

        elements.push(
          <div key={`test-scenario-${i}`} className="mb-6 p-4 bg-gradient-to-br from-green-50 to-blue-50 border border-green-200 rounded-xl shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <span className="text-green-600 text-lg">🧪</span>
              <h4 className="font-semibold text-gray-800">{t('featureManager.testScenario')}</h4>
            </div>
            <div className="space-y-2">
              {testSteps}
            </div>
          </div>
        );

        i = j;
        continue;
      }

      // Détection d'un tableau markdown
      if (/^\s*\|.*\|\s*$/.test(line)) {
        flushTextBlock();
        flushList();
        const { table, nextIdx } = parseMarkdownTable(lines, i);
        elements.push(table);
        i = nextIdx;
        continue;
      }

      if (line.startsWith("```")) {
        if (insideCodeBlock) {
          flushCodeBlock();
          insideCodeBlock = false;
        } else {
          flushTextBlock();
          flushList();
          insideCodeBlock = true;
        }
        i++;
        continue;
      }

      if (insideCodeBlock) {
        codeLines.push(line);
        i++;
        continue;
      }

      if (line === "") {
        flushTextBlock();
        flushList();
        i++;
        continue;
      }

      if (/^#+\s*/.test(line)) {
        flushTextBlock();
        flushList();
        const level = line.match(/^#+/)?.[0].length || 1;
        const headingContent = line.replace(/^#+\s*/, "");
        const headingIcon = level === 2 ? "🎯" : level === 3 ? "📋" : "🔹";

        elements.push(
          <div
            key={`heading-${i}`}
            className="mt-6 mb-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4 border-blue-500 rounded-r-lg"
          >
            <h3 className={`font-bold text-${level === 2 ? "xl" : level === 3 ? "lg" : "base"} text-gray-800 flex items-center gap-2`}>
              <span className="text-blue-600">{headingIcon}</span>
              {headingContent}
            </h3>
          </div>
        );
        i++;
        continue;
      }

      if (/^\d+\.\s+/.test(line)) {
        flushTextBlock();
        if (!insideOl) {
          insideOl = true;
          listItems = [];
        }
        const itemContent = line.replace(/^\d+\.\s+/, "");
        listItems.push(
          <li key={`li-${i}`} className="mb-2 p-3 bg-gray-50 rounded-lg border-l-3 border-green-400 hover:bg-gray-100 transition-colors">
            <div className="flex items-start gap-3">
              <span className="text-green-600 font-semibold">✓</span>
              <span className="text-gray-700 font-medium">{itemContent}</span>
            </div>
          </li>
        );
        i++;
        continue;
      }

      flushList();
      currentBlock.push(line);
      i++;
    }

    flushTextBlock();
    flushList();
    flushCodeBlock();

    return (
      <div className="relative bg-gradient-to-br from-white to-gray-50 border border-gray-200 rounded-xl shadow-lg overflow-hidden">
        {/* Header moderne avec dégradé */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-4 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                <span className="text-white text-lg">🤖</span>
              </div>
              <div>
                <h4 className="text-white font-semibold text-lg">{t('featureManager.aiResponse')}</h4>
                <p className="text-blue-100 text-sm">Analyse générée automatiquement</p>
              </div>
            </div>
            <div className="bg-white/10 rounded-lg p-2">
              <CopyButton textToCopy={response} />
            </div>
          </div>
        </div>

        {/* Contenu avec padding moderne */}
        <div className="p-6 space-y-4">
          {elements.length > 0 ? elements : (
            <div className="text-center py-8 text-gray-500">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">📝</span>
              </div>
              <p className="font-medium">{t('featureManager.responseGenerating')}</p>
            </div>
          )}
        </div>

        {/* Footer avec gradient subtil */}
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-3 border-t border-gray-200">
          <div className="flex items-center justify-between text-sm text-gray-600">
            <span className="flex items-center gap-2">
              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
              IA activée
            </span>
            <span>{new Date().toLocaleString()}</span>
          </div>
        </div>
      </div>
    );
  };

  useEffect(() => {
    setLoading(true);

    // Récupérer les nœuds
    apiClient.get(`/nodes`)
      .then((response) => {
        setNodes(response.data);
      })
      .catch((error) => {
        console.error("Erreur lors du chargement des nœuds :", error);
      });

    // Récupérer toutes les relations
    apiClient.get(`/relationships`)
      .then((response) => {
        setRelationships(response.data); // Stocker toutes les relations
      })
      .catch((error) => {
        console.error("Erreur lors du chargement des relations :", error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  // ── Insight Engine : charger les insights stockés au montage (sans relancer l'analyse) ──
  useEffect(() => {
    apiClient.get('/api/insights')
      .then(res => {
        if (res.data?.ready) {
          setInsights(res.data);
          insightsFetchedRef.current = true;
        }
      })
      .catch(err => console.warn('[InsightEngine] Chargement initial échoué:', err.message));
  }, []);

  const handleRefreshInsights = async () => {
    insightsFetchedRef.current = false;
    setInsightsLoading(true);
    try {
      const res = await apiClient.post('/api/insights/refresh');
      if (res.data?.ready) {
        setInsights(res.data);
        insightsFetchedRef.current = true;
      }
    } catch (err) {
      console.warn('[InsightEngine] Refresh échoué:', err.message);
    } finally {
      setInsightsLoading(false);
    }
  };

  // Fonction pour analyser le schéma via LangChain
  const analyzeSchema = () => {
    setLoading(true);

    let schema;

    if (selectedNode) {
      // Si un nœud est sélectionné, envoyer uniquement ce nœud et ses relations
      const relatedNodes = relationships
        .filter(
          (rel) =>
            rel.nodeName1 === selectedNode.name ||
            rel.nodeName2 === selectedNode.name
        )
        .map((rel) =>
          rel.nodeName1 === selectedNode.name ? rel.nodeName2 : rel.nodeName1
        );

      schema = {
        nodes: nodes.filter(
          (node) =>
            node.name === selectedNode.name || relatedNodes.includes(node.name)
        ),
        relationships: relationships.filter(
          (rel) =>
            rel.nodeName1 === selectedNode.name ||
            rel.nodeName2 === selectedNode.name
        ),
      };
    } else {
      // Sinon, envoyer tout le schéma (tous les nœuds et toutes les relations)
      schema = {
        nodes: nodes.map((node) => ({
          name: node.name,
          description: node.description,
          type: node.type,
          technology: node.technology,
          status: node.status,
          metadata: node.metadata,
          endpointSchema: node.endpointSchema,
        })),
        relationships: allRelationships.map((rel) => ({
          nodeName1: rel.nodeName1,
          nodeName2: rel.nodeName2,
          relationshipType: rel.relationshipType,
        })),
      };
    }

    // console.log("Schéma envoyé :", schema); // Log pour vérifier le contenu - COMMENTÉ pour sécurité
    function cleanSchemaForLLM(schema) {
      const nodes = (schema.nodes || []).map(node => {
        const { embedding, indexedText, testsJson, ...rest } = node;
        return {
          ...rest,
          description: rest.description?.slice(0, 1000),
        };
      });
      const relationships = (schema.relationships || []).map(rel => {
        const { embedding, indexedText, ...rest } = rel;
        return rest;
      });
      return { ...schema, nodes, relationships };
    }

    // Exemple d'envoi
    const cleanedSchema = cleanSchemaForLLM(schema);
    apiClient.post(`/analyze`, { schema: cleanedSchema, lang: i18n.language })
      .then((response) => {
        setSchemaAnalysis(response.data.analysis); // Stocker le résultat de l'analyse
        setLoading(false);
      })
      .catch((error) => {
        console.error("Erreur lors de l'analyse du schéma :", error);
        setLoading(false);
      });
  };

  // Fonction pour générer des tests via LangChain
  const generateTests = async () => {
    setLoading(true);
    const targetElement = document.getElementById("section-analysis");
    if (targetElement) {
      targetElement.scrollIntoView({ behavior: "smooth" });
    }

    let schema;

    try {
      // Récupérer les tests existants pour éviter les doublons
      const existingTestsResponse = await apiClient.get('/tests');
      const existingTests = existingTestsResponse.data || [];

      if (selectedNode) {
        // Si un nœud est sélectionné, récupérer ses tests spécifiques
        const nodeTestsResponse = await apiClient.get(`/node/${selectedNode.name}/tests`);
        const nodeTests = nodeTestsResponse.data || [];

        // Si un nœud est sélectionné, envoyer uniquement ce nœud et ses relations
        const relatedNodes = relationships
          .filter(
            (rel) =>
              rel.nodeName1 === selectedNode.name ||
              rel.nodeName2 === selectedNode.name
          )
          .map((rel) =>
            rel.nodeName1 === selectedNode.name ? rel.nodeName2 : rel.nodeName1
          );

        schema = {
          nodes: nodes.filter(
            (node) =>
              node.name === selectedNode.name || relatedNodes.includes(node.name)
          ).map((node) => ({
            ...node,
            existingTests: node.name === selectedNode.name ? nodeTests : []
          })),
          relationships: relationships.filter(
            (rel) =>
              rel.nodeName1 === selectedNode.name ||
              rel.nodeName2 === selectedNode.name
          ),
          existingTests: nodeTests
        };
      } else {
        // Sinon, envoyer tout le schéma (tous les nœuds et toutes les relations)
        schema = {
          nodes: nodes.map((node) => ({
            name: node.name,
            description: node.description,
            type: node.type,
            technology: node.technology,
            status: node.status,
            metadata: node.metadata,
            endpointSchema: node.endpointSchema,
            existingTests: existingTests.filter(test => test.nodeName === node.name)
          })),
          relationships: allRelationships.map((rel) => ({
            nodeName1: rel.nodeName1,
            nodeName2: rel.nodeName2,
            relationshipType: rel.relationshipType,
          })),
          existingTests: existingTests
        };
      }
    } catch (error) {
      console.warn("Erreur lors de la récupération des tests existants:", error);
      // Fallback vers l'ancien comportement sans tests existants
      if (selectedNode) {
        const relatedNodes = relationships
          .filter(
            (rel) =>
              rel.nodeName1 === selectedNode.name ||
              rel.nodeName2 === selectedNode.name
          )
          .map((rel) =>
            rel.nodeName1 === selectedNode.name ? rel.nodeName2 : rel.nodeName1
          );

        schema = {
          nodes: nodes.filter(
            (node) =>
              node.name === selectedNode.name || relatedNodes.includes(node.name)
          ),
          relationships: relationships.filter(
            (rel) =>
              rel.nodeName1 === selectedNode.name ||
              rel.nodeName2 === selectedNode.name
          ),
        };
      } else {
        schema = {
          nodes: nodes.map((node) => ({
            name: node.name,
            description: node.description,
            type: node.type,
            technology: node.technology,
            status: node.status,
            metadata: node.metadata,
            endpointSchema: node.endpointSchema,
          })),
          relationships: allRelationships.map((rel) => ({
            nodeName1: rel.nodeName1,
            nodeName2: rel.nodeName2,
            relationshipType: rel.relationshipType,
          })),
        };
      }
    }

    // console.log("Schéma envoyé pour les tests :", schema); // Log pour vérifier le contenu - COMMENTÉ pour sécurité

    try {
      const response = await apiClient.post(`/generate-tests`, { schema, lang: i18n.language });
      const testResult = response.data.test;
      setGeneratedTest(testResult);
      setLoading(false);
      
      // Retourner le résultat pour la modal
      return testResult;
    } catch (error) {
      console.error("Erreur lors de la génération des tests :", error);
      setLoading(false);
      throw error; // Propager l'erreur pour la modal
    }
  };

  const saveNode = async () => {
    if (!nodeName.trim()) return toast.error(t('featureManager.nameRequired'));

    if (selectedNode) {
      // Mise à jour d'un nœud existant - faire l'analyse d'impact
      const impact = await handleImpactRadar({
        type: "node",
        action: "update",
        name: selectedNode.name,
      });
      if (impact) {
        // Stocker l'action en attente au lieu d'utiliser window.confirm
        setPendingConfirmation({
          type: 'updateNode',
          action: () => executeNodeUpdate()
        });
        // La modal va s'afficher automatiquement avec le bouton de confirmation
      } else {
        // Pas d'impact détecté, procéder directement
        executeNodeUpdate();
      }
    } else {
      // Création d'un nouveau nœud - pas d'analyse d'impact nécessaire
      createNewNode();
    }
  };

  const executeNodeUpdate = () => {
    setLoading(true);
    apiClient.put(`/node/${selectedNode.name}`, {
      newName: nodeName,
      description: nodeDescription,
      type: nodeType,
      technology,
      featureTeam,  // 👈 NOUVEAU : Include featureTeam
      status,
      metadata: JSON.parse(metadata || "{}"),
      endpointSchema: JSON.parse(endpointSchema || "{}"),
    })
      .then((response) => {
        // Mettre à jour la liste des nœuds
        setNodes(
          nodes.map((n) =>
            n.name === selectedNode.name ? response.data : n
          )
        );

        // ✨ GARDER le nœud sélectionné mais avec les nouvelles données
        setSelectedNode(response.data);

        // Mettre à jour les champs du formulaire avec les nouvelles valeurs
        setNodeName(response.data.name);
        setNodeDescription(response.data.description);
        setNodeType(response.data.type);
        setTechnology(response.data.technology || '');
        setStatus(response.data.status || 'active');
        setMetadata(JSON.stringify(response.data.metadata || {}, null, 2));
        setEndpointSchema(JSON.stringify(response.data.endpointSchema || {}, null, 2));

        setLoading(false);
        setPendingConfirmation(null);

        // Message de confirmation
        console.log('✅ Nœud mis à jour avec succès et reste sélectionné');
      })
      .catch((error) => {
        console.error("Erreur lors de la mise à jour du nœud :", error);
        toast.error(t('featureManager.alerts.updateError', { message: error.message }));
        setLoading(false);
      });
  };

  const createNewNode = async () => {
    setLoading(true);
    
    try {
      // 🔍 NOUVEAU - Utiliser le système de détection intelligent Vector Echo
      const result = await createNodeWithConflictHandling({
        name: nodeName,
        description: nodeDescription,
        type: nodeType,
        technology,
        featureTeam,
        status,
        metadata: JSON.parse(metadata || "{}"),
        endpointSchema: JSON.parse(endpointSchema || "{}"),
      });

      // Si conflit détecté, le modal s'affiche automatiquement
      if (result.hasConflict) {
        console.log('⚠️ [FEATURE_MANAGER] Conflit détecté - modal affiché');
        setLoading(false);
        return;
      }

      // Si succès, continuer normalement
      if (result.success) {
        setNodes([...nodes, result.node]);
        resetForm();
        setPendingConfirmation(null);
        toast.success(t('components:enrichmentModal.alerts.nodeCreated'));
      }

    } catch (error) {
      console.error("❌ [FEATURE_MANAGER] Erreur lors de l'ajout du nœud:", error);
      toast.error(t('components:enrichmentModal.alerts.nodeCreationError', { 
        message: error.response?.data?.message || error.message 
      }));
    } finally {
      setLoading(false);
    }
  };
  
  // 🆕 Fonction pour enrichir un nœud existant (ancien système - conservé)
  const handleEnrichNode = async (proposal) => {
    setEnrichmentLoading(true);
    try {
      await apiClient.patch(`/node/${proposal.existingNode.name}/enrich`, proposal.newData);
      
      // Rafraîchir la liste des nœuds
      const response = await apiClient.get(`/nodes`);
      setNodes(response.data);
      
      toast.success(t('components:enrichmentModal.alerts.nodeEnriched', { name: proposal.existingNode.name }));
      setEnrichmentProposal(null);
      resetForm();
    } catch (error) {
      console.error("Erreur lors de l'enrichissement:", error);
      toast.error(t('components:enrichmentModal.alerts.enrichmentError', { 
        message: error.response?.data?.message || error.message 
      }));
    } finally {
      setEnrichmentLoading(false);
    }
  };

  // 🔍 NOUVEAU - Fonction pour résoudre un conflit (Vector Echo)
  const handleConflictResolution = async (resolution) => {
    try {
      console.log('🔄 [FEATURE_MANAGER] Résolution du conflit:', resolution.action);
      
      const result = await resolveConflict(resolution);
      
      if (result.success) {
        // Rafraîchir la liste des nœuds
        const response = await apiClient.get(`/nodes`);
        setNodes(response.data);
        
        const message = resolution.action === 'enrich'
          ? t('components:enrichmentModal.alerts.nodeEnriched', { name: result.node.name })
          : t('components:enrichmentModal.alerts.nodeCreated');
        
        toast.success(message);
        resetForm();
      }
      
    } catch (error) {
      console.error('❌ [FEATURE_MANAGER] Erreur résolution:', error);
      toast.error(t('components:enrichmentModal.alerts.enrichmentError', { 
        message: error.response?.data?.message || error.message 
      }));
    }
  };

  const [acceptanceCriteria, setAcceptanceCriteria] = useState([]);
  const [editingCriteria, setEditingCriteria] = useState(null);
  const [editValues, setEditValues] = useState({
    criteria: "",
    visual: "",
  });

  {
    /*TODO voir connnexion avec confluence pour créer de la documentation ?*/
  }
  {
    /*TODO Ajouter une option de traitement par le LLM pour le texte / Gherkin et créer un ou plusieurs noeuds en fonction de l'analyse, demander confirmation a l'user ?*/
  }

  // Charger les critères lors de la sélection d'un nœud
  const selectNode = (node) => {
    setSelectedNode(node);
    // Persister dans localStorage
    localStorage.setItem('selectedNode', JSON.stringify(node));
    
    setNodeName(node.name);
    setNodeDescription(node.description);
    setNodeType(node.type);
    setTechnology(node.technology);
    setFeatureTeam(node.featureTeam || "");  // 👈 NOUVEAU : Set feature team
    setStatus(node.status);
    setMetadata(node.metadata ? JSON.stringify(node.metadata, null, 2) : "{}");
    setEndpointSchema(
      node.endpointSchema ? JSON.stringify(node.endpointSchema, null, 2) : "{}"
    );

    setLoading(true);
    apiClient.get(`/relationships/${node.name}`)
      .then((response) => {
        setRelationships(response.data);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Erreur lors du chargement des relations :", error);
        setLoading(false);
      });

    // Charger les critères d'acceptation
    apiClient.get(`/node/${node.name}/acceptance-criteria`)
      .then((res) => setAcceptanceCriteria(res.data))
      .catch(() => setAcceptanceCriteria([]));

    // Charger les tests du nœud sélectionné SANS toucher à testScenarios global
    apiClient.get(`/node/${node.name}/tests`)
      .then((response) => setNodeTests(response.data))
      .catch(() => setNodeTests([]));
  };

  const resetForm = () => {
    setNodeName("");
    setNodeDescription("");
    setNodeType("");
    setTechnology("");
    setFeatureTeam("");  // 👈 NOUVEAU : Reset feature team
    setStatus("");
    setMetadata("{}");  // 👈 Vider aussi les métadonnées
    setEndpointSchema("{}");  // 👈 Vider aussi le schéma d'endpoints
  };

  // === FONCTIONS POUR LE CRÉATEUR DE TESTS MANUELS ===
  const handleOpenManualTestCreator = () => {
    setShowManualTestCreator(true);
    setEditingManualTest(null);
  };

  // === FONCTION POUR LA GÉNÉRATION IA DE TESTS ===
  const handleOpenTestGeneration = () => {
    setShowTestGenerationModal(true);
  };

  const handleEditManualTest = (testData) => {
    setEditingManualTest(testData);
    setShowManualTestCreator(true);
  };

  const handleSaveManualTest = (testData) => {
    // Ajouter le test aux éléments en modélisation
    addPendingNode(testData);

    // 👈 NOUVEAU: Ajouter aussi aux testScenarios pour intégration immédiate avec GlobalTestsGraph
    if (testData.nodeName || testData.targetNode) {
      const scenarioForGlobalView = {
        id: testData.id,
        title: testData.title,
        description: testData.description,
        type: testData.metadata?.category || 'functional',
        steps: testData.steps || [],
        priority: testData.priority || 'medium',
        positive: testData.positive !== false,
        nodeName: testData.nodeName || testData.targetNode,
        code: testData.code || '',
        tags: testData.tags || [],
        isManuallyCreated: true
      };

      setTestScenarios(prev => {
        // Éviter les doublons
        const existingIndex = prev.findIndex(s => s.id === testData.id);
        if (existingIndex >= 0) {
          const updated = [...prev];
          updated[existingIndex] = scenarioForGlobalView;
          return updated;
        }
        return [...prev, scenarioForGlobalView];
      });
    }

    // Fermer la modal
    setShowManualTestCreator(false);
    setEditingManualTest(null);

    // Message de confirmation
    toast.success(t('featureManager.alerts.testCreated'));
  };

  const handleCancelManualTest = () => {
    setShowManualTestCreator(false);
    setEditingManualTest(null);
  };

  // === FONCTIONS POUR LE GÉNÉRATEUR DE SPÉCIFICATIONS ===
  const handleOpenSpecificationGenerator = () => {
    setShowSpecificationGenerator(true);
  };

  const handleCloseSpecificationGenerator = () => {
    setShowSpecificationGenerator(false);
  };

  // === FONCTIONS POUR LE GUIDE DMBT ===
  const handleOpenDMBTGuide = () => {
    //console.log('🧠 Ouverture du guide DMBT');
    setShowDMBTGuide(true);
  };

  const handleCloseDMBTGuide = () => {
    console.log('🧠 Fermeture du guide DMBT');
    setShowDMBTGuide(false);
  };

  const handleSpecificationGenerated = async (specification) => {
    // Créer la nouvelle spécification avec ID et date
    const newSpecification = {
      ...specification,
      id: Date.now(),
      createdAt: new Date().toISOString()
    };

    // 1. Sauvegarder via API si possible
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/specifications/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          specification: {
            specification: newSpecification  // Wrap the specification correctly
          },
          saveAsNode: true
        })
      });

      if (response.ok) {
        const result = await response.json();
        console.log('✅ Spécification sauvegardée via API:', result.specification?.title);

        // Utiliser l'ID de la base de données
        newSpecification.id = result.specification.id;

        // Rafraîchir la liste des spécifications
        if (specificationsRefreshRef.current) {
          console.log('🔄 Rafraîchissement de la liste des spécifications...');
          specificationsRefreshRef.current();
        }
      }
    } catch (error) {
      console.error('❌ Erreur sauvegarde API, fallback localStorage:', error);
    }

    // 2. Ajouter à la liste locale dans tous les cas
    setGeneratedSpecifications(prev => {
      const updatedSpecs = [...prev, newSpecification];

      // Sauvegarder immédiatement dans localStorage
      try {
        localStorage.setItem('generatedSpecifications', JSON.stringify(updatedSpecs));
        console.log('📱 Spécification sauvegardée dans localStorage:', newSpecification.specification?.title);
      } catch (error) {
        console.error('❌ Erreur sauvegarde localStorage:', error);
      }

      return updatedSpecs;
    });

    // Optionnel : créer automatiquement un nœud avec la spécification
    if (specification.specification && specification.specification.title) {
      const specNode = {
        id: Date.now(),
        name: specification.specification.title,
        description: specification.specification.functionalDescription || specification.specification.summary,
        type: 'Specification',
        technology: 'Product Requirements',
        status: 'draft',
        isActualNode: true,  // 👈 NOUVEAU : Flag explicite pour identifier les vrais nœuds
        author: user?.name || 'Product Owner',
        metadata: {
          specificationData: specification,
          businessValue: specification.specification.businessValue,
          userStories: specification.specification.userStories,
          acceptanceCriteria: specification.specification.acceptanceCriteria,
          functionalRequirements: specification.specification.functionalRequirements,
          nonFunctionalRequirements: specification.specification.nonFunctionalRequirements,
          testCases: specification.specification.testCases,
          dependencies: specification.specification.dependencies,
          risks: specification.specification.risks,
          estimations: specification.specification.estimations,
          generatedBy: 'AI',
          sourceType: 'specification-generator',
          createdAt: new Date().toISOString()
        },
        createdAt: new Date().toISOString()
      };

      addPendingNode(specNode);

      // Message de confirmation
      toast.success(t('featureManager.alerts.specificationGenerated', { title: specification.specification.title }));
    }
  };

  const handleExportSpecification = (specification) => {
    try {
      const dataStr = JSON.stringify(specification, null, 2);
      const blob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `specification-${specification.specification?.title?.replace(/[^a-z0-9]/gi, '-') || 'unnamed'}-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      console.log('📥 Spécification exportée:', specification.specification?.title || 'Sans titre');
    } catch (error) {
      console.error('❌ Erreur lors de l\'export de la spécification:', error);
      toast.error(t('featureManager.alerts.exportError'));
    }
  };

  // TODO: FEATURE EN RÉFLEXION - Génération automatique de nœuds depuis spécifications
  // ================================================================================
  // QUESTIONNEMENTS À RÉSOUDRE :
  // - Workflow optimal : quand créer les nœuds ? (automatique vs manuel)
  // - Types de nœuds : Feature vs Specification vs Test - quelle granularité ?
  // - Relations automatiques : comment les gérer proprement ?
  // - UX : comment éviter la confusion utilisateur entre export et génération ?
  // - Validation : l'utilisateur doit-il pouvoir modifier avant création ?
  // 
  // IDÉES D'AMÉLIORATION :
  // - Preview des nœuds avant création
  // - Sélection granulaire (Feature only, Tests only, etc.)
  // - Templates de nœuds configurables
  // - Intégration avec le système de relations existant
  // ================================================================================

  /* COMMENTÉ - En réflexion pour amélioration du workflow
  const handleCreateNodesFromSpecification = (specification) => {
    if (!specification.specification) {
      toast.error('❌ Spécification invalide');
      return;
    }

    const spec = specification.specification;
    let nodesCreated = 0;

    // 1. Créer le nœud Feature principal
    const mainFeatureNode = {
      id: Date.now(),
      name: spec.title,
      description: spec.functionalDescription || spec.summary,
      type: 'Feature',
      technology: 'Product Requirements',
      status: 'draft',
      isActualNode: true,  // 👈 NOUVEAU : Flag explicite pour identifier les vrais nœuds
      metadata: {
        businessValue: spec.businessValue,
        estimations: spec.estimations,
        risks: spec.risks,
        sourceSpecification: specification.id,
        generatedBy: 'AI-Specification'
      }
    };
    addPendingNode(mainFeatureNode);
    nodesCreated++;

    // 2. Créer des nœuds Test pour chaque critère d'acceptation
    if (spec.acceptanceCriteria && spec.acceptanceCriteria.length > 0) {
      spec.acceptanceCriteria.forEach((criteria, index) => {
        const testNode = {
          id: Date.now() + index + 1,
          name: `Test: ${criteria.title}`,
          description: criteria.description,
          type: 'Test',
          targetNode: spec.title, // 👈 LIEN vers la feature
          steps: criteria.gherkin ? [
            ...(criteria.gherkin.given?.map(g => `Given ${g}`) || []),
            ...(criteria.gherkin.when?.map(w => `When ${w}`) || []),
            ...(criteria.gherkin.then?.map(t => `Then ${t}`) || [])
          ] : [],
          metadata: {
            scenarioId: criteria.scenarioId,
            priority: criteria.priority,
            sourceSpecification: spec.title,
            gherkinScenario: criteria.gherkin,
            testType: 'acceptance'
          }
        };
        addPendingNode(testNode);
        nodesCreated++;
      });
    }

    // 3. Optionnel : Créer des User Stories
    if (spec.userStories && spec.userStories.length > 0) {
      spec.userStories.forEach((story, index) => {
        const storyNode = {
          id: Date.now() + index + 100,
          name: `US: ${story.title}`,
          description: story.story,
          type: 'UserStory',
          targetNode: spec.title,
          isActualNode: true,  // 👈 NOUVEAU : Flag explicite pour identifier les vrais nœuds
          metadata: {
            storyId: story.id,
            priority: story.priority,
            estimation: story.estimation,
            parentFeature: spec.title
          }
        };
        addPendingNode(storyNode);
        nodesCreated++;
      });
    }

    toast.success(`✅ ${nodesCreated} nœuds créés automatiquement depuis "${spec.title}" !`);
  };
  */

  const handleDeleteSpecification = (specificationId) => {
    if (!window.confirm(t('featureManager.confirmDeleteSpec'))) {
      return;
    }

    setGeneratedSpecifications(prev => {
      const updatedSpecs = prev.filter(spec => spec.id !== specificationId);

      // Sauvegarder immédiatement dans localStorage
      try {
        localStorage.setItem('generatedSpecifications', JSON.stringify(updatedSpecs));
        console.log('🗑️ Spécification supprimée et localStorage mis à jour');
      } catch (error) {
        console.error('❌ Erreur suppression localStorage:', error);
      }

      return updatedSpecs;
    });
  };

  const handleClearAllSpecifications = () => {
    if (!window.confirm(t('featureManager.confirmDeleteAllSpecs'))) {
      return;
    }

    setGeneratedSpecifications([]);

    try {
      localStorage.removeItem('generatedSpecifications');
      console.log('🧹 Toutes les spécifications supprimées');
      toast.success(t('featureManager.allSpecsDeleted'));
    } catch (error) {
      console.error('❌ Erreur nettoyage localStorage:', error);
    }
  };

  const addRelationship = () => {
    if (!selectedNode || !targetNode)
      return toast.error(t('featureManager.selectSourceAndTarget'));

    // Vérifier le type de relation
    if (!relationshipType) return toast.error(t('featureManager.selectRelationType'));

    // Si type est CUSTOM, vérifier que le champ personnalisé n'est pas vide
    if (relationshipType === "CUSTOM" && !customRelationshipType.trim()) {
      return toast.error(t('featureManager.alerts.pleaseEnterCustomRelationType'));
    }

    // Définir le type final à utiliser
    const finalRelationshipType =
      relationshipType === "CUSTOM"
        ? customRelationshipType.trim()
        : relationshipType;

    setLoading(true);
    apiClient.post(`/node/relationship`, {
      nodeName1: selectedNode.name,
      nodeName2: targetNode,
      relationshipType: finalRelationshipType,
    })
      .then((response) => {
        setRelationships([...relationships, response.data]);

        // Réinitialiser le champ personnalisé
        if (relationshipType === "CUSTOM") {
          setCustomRelationshipType("");
          setRelationshipType("");
        }

        toast.success(t('featureManager.alerts.relationCreated'));
        setLoading(false);
      })
      .catch((error) => {
        console.error("Erreur lors de la création de la relation :", error);
        setLoading(false);
      });
  };

  // Pour la suppression de relation
  const deleteRelationship = async (nodeName1, nodeName2, relationshipType) => {
    // Activer le loading dès le début de la fonction
    setLoading(true);

    try {
      const impact = await handleImpactRadar({
        type: "relation",
        action: "delete",
        nodeName1,
        nodeName2,
        relationshipType,
      });

      if (impact) {
        // Affiche la modale, puis demande confirmation avant de continuer
        if (
          window.confirm(
            t('featureManager.confirmDeleteRelationImpact')
          )
        ) {
          try {
            await apiClient.delete(`/node/relationship`, {
              data: { nodeName1, nodeName2, relationshipType },
            });

            setRelationships(
              relationships.filter(
                (rel) =>
                  !(
                    rel.nodeName1 === nodeName1 &&
                    rel.nodeName2 === nodeName2 &&
                    rel.relationshipType === relationshipType
                  )
              )
            );
          } catch (error) {
            toast.error(
              "Erreur lors de la suppression de la relation: " + error.message
            );
          }
        }
      } else {
        // Si pas d'impact, suppression directe
        try {
          await apiClient.delete(`/node/relationship`, {
            data: { nodeName1, nodeName2, relationshipType },
          });

          setRelationships(
            relationships.filter(
              (rel) =>
                !(
                  rel.nodeName1 === nodeName1 &&
                  rel.nodeName2 === nodeName2 &&
                  rel.relationshipType === relationshipType
                )
            )
          );
        } catch (error) {
          toast.error(
            "Erreur lors de la suppression de la relation: " + error.message
          );
        }
      }
    } catch (error) {
      console.error("Erreur lors de l'analyse d'impact:", error);
      toast.error(t('featureManager.alerts.impactAnalysisError', { message: error.message }));
    } finally {
      // Toujours réinitialiser l'état loading à la fin
      setLoading(false);
    }
  };

  const [loadingNode, setLoadingNode] = useState(null);

  const deleteNode = async (name) => {
    setLoadingNode(name);
    const impact = await handleImpactRadar({
      type: "node",
      action: "delete",
      name,
    });
    if (impact) {
      setConfirmImpact({ action: "delete", nodeName: name });
      setLoadingNode(null);
    } else {
      setLoadingNode(null);
    }
  };

  const resetSelection = () => {
    setSelectedNode(null);
    localStorage.removeItem('selectedNode'); // Nettoyer le localStorage
    setRelationships(allRelationships); // Restaurer toutes les relations globales
    resetForm(); // Réinitialiser le formulaire
  };

  const toggleNodeSelection = (node) => {
    if (selectedNode?.name === node.name) {
      // Si le nœud est déjà sélectionné, désélectionner
      resetSelection();
    } else {
      // Sinon, sélectionner le nœud
      selectNode(node);
    }
  };
  useEffect(() => {
    if (llmResponse && typeof llmResponse === "string") {
      saveSessionData("lastLlmTestResponse", llmResponse);
      if (selectedNode) {
        saveSessionData("lastLlmTestNode", selectedNode);
      }
    }
  }, [llmResponse, selectedNode]);

  // Fonction pour effacer la dernière génération de tests stockée
  const clearLastLlmTestResponse = () => {
    localStorage.removeItem("lastLlmTestResponse");
    localStorage.removeItem("lastLlmTestNode");
    setGeneratedTest("");
  };
  // Tabs component
  const Tabs = ({ children }) => {
    const [activeTab, setActiveTab] = useState(0);

    // Filtrer pour ne garder que les éléments de type Tab
    const tabs = React.Children.toArray(children);

    return (
      <div>
        <TabGroup>
          {tabs.map((tab, index) => (
            <Tab
              key={index}
              active={activeTab === index}
              onClick={() => setActiveTab(index)}
            >
              {tab.props.title}
            </Tab>
          ))}
        </TabGroup>
        <div className="mt-4">
          {tabs[activeTab] && tabs[activeTab].props.children}
        </div>
      </div>
    );
  };

  // Définir un sous-composant Tab pour l'usage dans JSX
  Tabs.Tab = ({ title, children }) => children;

  const fetchNodes = () => {
    setLoading(true);
    Promise.all([
      apiClient.get(`/nodes`),
      apiClient.get(`/relationships`),
      apiClient.get(`/test-scenarios`),
    ]).then(([nodesResponse, relationshipsResponse, testsResponse]) => {
      const freshNodes = nodesResponse.data;
      setNodes(freshNodes);
      setRelationships(relationshipsResponse.data);
      setAllRelationships(relationshipsResponse.data);
      setTestScenarios(testsResponse.data);
      
      // 🔍 DEBUG - Valider le selectedNode : vérifier s'il existe encore
      const currentSelectedNode = selectedNode;
      console.log('🔍 fetchNodes - Validation selectedNode:', {
        currentSelectedNode: currentSelectedNode?.name,
        freshNodesCount: freshNodes.length,
        freshNodesNames: freshNodes.map(n => n.name).slice(0, 5) // Premier 5 noms
      });
      
      if (currentSelectedNode) {
        const existsInFreshData = freshNodes.find(node => 
          node.name === currentSelectedNode.name || 
          node.id === currentSelectedNode.id
        );
        
        if (!existsInFreshData) {
          console.log('🧹 SelectedNode obsolète détecté, nettoyage...', {
            cached: currentSelectedNode.name,
            exists: false
          });
          setSelectedNode(null);
          localStorage.removeItem('selectedNode');
        } else {
          // 🚫 TEMPORAIREMENT DÉSACTIVÉ - Mise à jour automatique avec données fraîches
          console.log('⚠️ SelectedNode trouvé dans les données fraîches mais mise à jour désactivée:', {
            current: currentSelectedNode.name,
            fresh: existsInFreshData.name,
            dataChanged: JSON.stringify(currentSelectedNode) !== JSON.stringify(existsInFreshData)
          });
          /*
          // Mettre à jour avec les données fraîches
          console.log('✅ SelectedNode validé et mis à jour', existsInFreshData.name);
          setSelectedNode(existsInFreshData);
          localStorage.setItem('selectedNode', JSON.stringify(existsInFreshData));
          */
        }
      }
      
      setLoading(false);
    }).catch(error => {
      console.error('Erreur lors du chargement des données:', error);
      setLoading(false);
    });
  };

  // 🧠 SMART TOOLS: Détecter la navigation depuis Smart Tools pour auto-ouvrir TestGeneration
  useEffect(() => {
    if (location.state?.openTestGeneration && location.state?.selectedNodeId && nodes.length > 0) {
      const nodeId = location.state.selectedNodeId;
      const nodeName = location.state.selectedNodeName;
      
      console.log('🧠 [SMART_TOOLS] Navigation détectée - nodeId:', nodeId, 'nodeName:', nodeName);
      
      // Trouver le nœud par ID (Neo4j internal ID)
      const targetNode = nodes.find(n => {
        // L'ID Neo4j peut être dans n.id ou obtenu via id(n)
        return Number(n.id) === Number(nodeId) || n.name === nodeName;
      });
      
      if (targetNode) {
        console.log('🧠 [SMART_TOOLS] Nœud trouvé:', targetNode.name);
        setSelectedNode(targetNode);
        localStorage.setItem('selectedNode', JSON.stringify(targetNode));
        setShowTestGenerationModal(true);
        
        // Nettoyer le state pour éviter la réouverture au refresh
        window.history.replaceState({}, document.title);
      } else {
        console.warn('🧠 [SMART_TOOLS] Nœud non trouvé - ID:', nodeId);
        toast.warning(t('smartTools.nodeNotFound') || 'Nœud non trouvé. Veuillez le sélectionner manuellement.');
      }
    }
  }, [location.state, nodes, t]);

  const totalNodes = nodes.length;
  const nodesWithTests = nodes.filter(
    (node) => getTestsCount(node.testsJson) > 0
  ).length;
  const coverage =
    totalNodes > 0 ? Math.round((nodesWithTests / totalNodes) * 100) : 0;

  const [duplicateTestIds, setDuplicateTestIds] = useState([]);

  // Détection des doublons (par contenu)
  const detectDuplicateTests = useCallback((scenarios) => {
    const signatures = {};
    const duplicates = [];
    scenarios.forEach((scenario) => {
      const signature = JSON.stringify({
        title: scenario.title || "",
        description: scenario.description || "",
        steps: Array.isArray(scenario.steps)
          ? scenario.steps
            .map((s) => (typeof s === "string" ? s : s.text || ""))
            .join("")
          : "",
      });
      if (signatures[signature]) {
        if (!duplicates.includes(signatures[signature]))
          duplicates.push(signatures[signature]);
        if (!duplicates.includes(scenario.id)) duplicates.push(scenario.id);
      } else {
        signatures[signature] = scenario.id;
      }
    });
    setDuplicateTestIds(duplicates);
  }, []);

  // Détecter les doublons à chaque changement de testScenarios
  useEffect(() => {
    if (testScenarios && testScenarios.length > 0) {
      detectDuplicateTests(testScenarios);
    } else {
      setDuplicateTestIds([]);
    }
  }, [testScenarios, detectDuplicateTests]);

  // Suppression d'un doublon
  const removeDuplicateTest = (id) => {
    if (window.confirm(t('featureManager.confirmDeleteDuplicate'))) {
      setLoading(true);
      const scenarioToRemove = testScenarios.find((s) => s.id === id);
      if (!scenarioToRemove) {
        setLoading(false);
        return;
      }
      apiClient.post(`/add-scenarios`, {
        scenarios: [],
        relatedNodeName: scenarioToRemove.nodeName || t('featureManager.notAssociated'),
        operation: "deleteOnly",
        scenarioIds: [id],
      })
        .then(() => {
          // Recharge la liste complète depuis l'API
          apiClient.get(`/tests`)
            .then((response) => {
              setTestScenarios(response.data);
              setLoading(false);
            })
            .catch((error) => {
              toast.error(t('featureManager.alerts.testRefreshError'));
              setLoading(false);
            });
        })
        .catch((error) => {
          toast.error(t('featureManager.alerts.duplicateDeleteError'));
          setLoading(false);
        });
    }
  };

  // Fonction pour mettre à jour un scénario
  const handleUpdateScenario = async (updatedScenario) => {
    try {
      setLoading(true);

      // Utiliser la route existante add-scenarios pour mettre à jour
      const response = await apiClient.post(`/add-scenarios`, {
        scenarios: [updatedScenario], // Tableau avec le scénario mis à jour
        relatedNodeName: updatedScenario.nodeName || t('featureManager.notAssociated'),
        operation: "update" // Opération de mise à jour
      });

      if (response.data.success) {
        // Mettre à jour la liste locale des scénarios
        setTestScenarios(prevScenarios =>
          prevScenarios.map(scenario =>
            scenario.id === updatedScenario.id
              ? { ...scenario, ...updatedScenario }
              : scenario
          )
        );

        // Recharger depuis l'API pour s'assurer de la cohérence
        const refreshResponse = await apiClient.get(`/tests`);
        setTestScenarios(refreshResponse.data);

        setLoading(false);
        return { success: true };
      } else {
        throw new Error(response.data.message || t('featureManager.errors.updateError'));
      }
    } catch (error) {
      console.error('Erreur lors de la mise à jour du scénario:', error);
      setLoading(false);
      throw new Error(error.response?.data?.message || t('featureManager.errors.scenarioUpdateImpossible'));
    }
  };

  const [pendingNodes, setPendingNodes] = useState([]);

  // Ajout d'un pending node (doit être défini avant tout useEffect qui l'utilise)

  // Charger les pendingNodes au démarrage avec approche hybride intelligente
  useEffect(() => {
    const initializePendingNodes = async () => {
      try {
        const pendingNodesData = await loadPendingNodes();
        setPendingNodes(pendingNodesData);
      } catch (error) {
        console.error('❌ Erreur initialisation pending nodes:', error);
        // Fallback sur un tableau vide en cas d'erreur
        setPendingNodes([]);
      }
    };

    initializePendingNodes();
  }, []);
  
  // AJOUT : état pour l'édition
  const [editingPendingNode, setEditingPendingNode] = useState(null);
  /*
  // Charger les pendingNodes au démarrage (corrigé : un seul useEffect)
  useEffect(() => {
    const stored = localStorage.getItem("pendingNodes");
    if (stored) setPendingNodes(JSON.parse(stored));
  }, []);
*/

  useEffect(() => {
    if (selectedNode) {
      setNodeName(selectedNode.name || "");
      setNodeDescription(selectedNode.description || "");
      setNodeType(selectedNode.type || "");
      setTechnology(selectedNode.technology || "");
      setStatus(selectedNode.status || "");
      setMetadata(selectedNode.metadata ? JSON.stringify(selectedNode.metadata, null, 2) : "{}");
      setEndpointSchema(selectedNode.endpointSchema ? JSON.stringify(selectedNode.endpointSchema, null, 2) : "{}");
    }
  }, [selectedNode]);

  // Sauvegarder à chaque changement avec utilitaire spécialisé
  useEffect(() => {
    savePendingNodes(pendingNodes);
  }, [pendingNodes]);

  // Sauvegarder les spécifications générées à chaque changement
  useEffect(() => {
    try {
      localStorage.setItem("generatedSpecifications", JSON.stringify(generatedSpecifications));
      console.log('📋 Spécifications sauvegardées dans localStorage:', generatedSpecifications.length);
    } catch (e) {
      console.error('❌ Erreur sauvegarde spécifications:', e);
    }
  }, [generatedSpecifications]);




  // ===== FONCTIONS UTILITAIRES POUR LA VALIDATION DES TESTS =====
  
  /**
   * Extrait le nom du nœud depuis le titre du test
   */
  const extractNodeNameFromTitle = (title) => {
    if (!title) return null;
    
    // Patterns courants dans les titres de tests
    const patterns = [
      /Test:?\s+(.+?)(?:\s|$)/i,           // "Test: NodeName"
      /Test\s+pour\s+(.+?)(?:\s|$)/i,     // "Test pour NodeName"
      /Validation\s+(.+?)(?:\s|$)/i,      // "Validation NodeName"
      /(.+?)\s+-\s+Test/i,                // "NodeName - Test"
      /^(.+?)\s+test/i                    // "NodeName test"
    ];
    
    for (const pattern of patterns) {
      const match = title.match(pattern);
      if (match && match[1]) {
        const nodeName = match[1].trim();
        // Vérifier si ce nom correspond à un nœud existant
        if (nodes.some(node => node.name === nodeName)) {
          return nodeName;
        }
      }
    }
    return null;
  };

  /**
   * Extrait le nom du nœud depuis la description du test
   */
  const extractNodeNameFromDescription = (description) => {
    if (!description) return null;
    
    // Chercher des références explicites aux nœuds
    const nodeNames = nodes.map(node => node.name);
    for (const nodeName of nodeNames) {
      if (description.includes(nodeName)) {
        return nodeName;
      }
    }
    return null;
  };

  /**
   * Valide qu'un test a toutes les informations nécessaires
   */
  const validateTestStructure = (test) => {
    const issues = [];
    
    if (!test.name && !test.title) {
      issues.push('Nom/titre manquant');
    }
    
    if (!test.targetNode && !test.nodeName && !test.metadata?.targetNode && 
        !test.associatedNodes?.length && !test.nodesInvolved?.length) {
      issues.push('Nœud cible manquant');
    }
    
    if (!test.steps || !Array.isArray(test.steps) || test.steps.length === 0) {
      issues.push('Étapes de test manquantes');
    }
    
    return {
      isValid: issues.length === 0,
      issues: issues
    };
  };

  // Ajoute ce helper dans FeatureManager.js
  const handleValidateTestNode = async (pendingTestNode) => {
    try {
      setLoading(true);
      // console.log('🔍 Validation du test node:', pendingTestNode); // COMMENTÉ - contient données sensibles

      // 🔍 RECHERCHE EXHAUSTIVE DU NŒUD CIBLE - Couvre tous les cas possibles
      let relatedNodeName = 
        // Champs principaux (tests manuels)
        pendingTestNode.targetNode ||
        pendingTestNode.nodeName ||
        pendingTestNode.sourceNode ||
        
        // Métadonnées (tous types de tests)
        pendingTestNode.metadata?.sourceNode ||
        pendingTestNode.metadata?.targetNode ||
        pendingTestNode.metadata?.nodeName ||
        pendingTestNode.metadata?.relatedNode ||
        
        // Tests IA (ImpactRadarModal)
        pendingTestNode.associatedNodes?.[0] ||
        pendingTestNode.nodesInvolved?.[0] ||
        pendingTestNode.relatedNode ||
        
        // Tests de spécifications
        pendingTestNode.metadata?.scenarios?.[0]?.nodeName ||
        
        // Fallback - extraire du titre/description
        extractNodeNameFromTitle(pendingTestNode.title || pendingTestNode.name) ||
        extractNodeNameFromDescription(pendingTestNode.description);

      if (!relatedNodeName) {
        console.warn('❌ Aucun nœud cible trouvé pour le test:', {
          id: pendingTestNode.id,
          name: pendingTestNode.name,
          title: pendingTestNode.title,
          availableFields: Object.keys(pendingTestNode),
          metadata: pendingTestNode.metadata ? Object.keys(pendingTestNode.metadata) : null
        });
        toast.error(t('featureManager.alerts.noTargetNodeFound'));
        setLoading(false);
        return;
      }

      // Vérifier que le nœud existe réellement
      const nodeExists = nodes.some(node => node.name === relatedNodeName);
      if (!nodeExists) {
        console.warn('❌ Nœud cible introuvable dans le graphe:', relatedNodeName);
        toast.error(`Le nœud cible "${relatedNodeName}" n'existe pas dans le graphe. Veuillez vérifier le nom.`);
        setLoading(false);
        return;
      }

      console.log('✅ Nœud cible trouvé:', relatedNodeName);

      debugLog('✅ Nœud cible trouvé:', relatedNodeName);

      // Préparer le scénario avec une structure correcte
      const scenarioToAdd = {
        id: pendingTestNode.id || `test_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        title: pendingTestNode.name || pendingTestNode.title || `Test automatique ${Date.now()}`,
        type: pendingTestNode.type || pendingTestNode.metadata?.scenarioType || "acceptance",
        description: pendingTestNode.description || "",
        steps: pendingTestNode.metadata?.steps || pendingTestNode.steps || [],
        positive: pendingTestNode.metadata?.positive !== false,
        category: pendingTestNode.metadata?.category || "functional",
        priority: pendingTestNode.metadata?.priority || "medium",
        code: pendingTestNode.metadata?.code || "",
        author: pendingTestNode.author || "LLM",
        createdAt: pendingTestNode.createdAt || new Date().toISOString()
      };

      // console.log('🔍 Scénario préparé:', scenarioToAdd); // COMMENTÉ - contient données sensibles

      // Envoie le test/scénario à l'API d'ajout de tests
      const response = await apiClient.post(`/add-scenarios`, {
        scenarios: [scenarioToAdd],
        relatedNodeName,
      });

      // Afficher les détails de l'opération
      if (response.data.details) {
        const { details } = response.data;
        let message = `Test intégré avec succès !\n`;
        message += `• Tests existants: ${details.existingTests}\n`;
        message += `• Nouveaux tests: ${details.newTests}\n`;
        if (details.duplicatesIgnored > 0) {
          message += `• Doublons ignorés: ${details.duplicatesIgnored}\n`;
        }
        message += `• Total tests: ${details.totalTests}`;
        toast.success(message);
      } else {
        toast.success(t('featureManager.alerts.testIntegratedSuccess'));
      }

      // Mets à jour l'affichage
      setPendingNodes((prev) => prev.filter((n) => n.id !== pendingTestNode.id));
      // Rafraîchit le lab (nodes/tests)
      await fetchNodes();
      setLoading(false);
    } catch (e) {
      setLoading(false);
      console.error('❌ Erreur lors de la validation:', e);
      toast.error(
        t('featureManager.alerts.testIntegrationError') +
        (e.response?.data?.error || e.message)
      );
    }
  };
  // Valider (intégrer au graphe)
  const handleValidateNode = async (node) => {
    try {
      setLoading(true);
      // Appel API pour créer le nœud dans la base de connaissance
      await apiClient.post(`/node`, {
        ...node,
        endpointSchema: node.endpointSchema || {},
      });
      // Mets à jour la liste des nœuds affichés
      setNodes((prev) => [...prev, node]);
      // Retire le nœud des pendingNodes
      setPendingNodes((prev) => prev.filter((n) => n.id !== node.id));
      // Rafraîchit le lab (nodes/tests)
      await fetchNodes();
      setLoading(false);
      toast.success(t('featureManager.alerts.nodeIntegratedSuccess'));
    } catch (e) {
      setLoading(false);
      toast.error(
        t('featureManager.alerts.nodeIntegrationError') +
        (e.response?.data?.error || e.message)
      );
    }
  };

  const updateNodeSimple = async () => {
    if (!nodeName.trim()) return toast.error(t('featureManager.alerts.nameRequired'));
    setLoading(true);
    try {
      const response = await apiClient.put(`/node/${selectedNode.name}`, {
        newName: nodeName,
        description: nodeDescription,
        type: nodeType,
        technology,
        featureTeam,  // 👈 FIX: Ajout du champ featureTeam manquant !
        status,
        metadata: JSON.parse(metadata || "{}"),
        endpointSchema: JSON.parse(endpointSchema || "{}"),
      });
      setNodes(
        nodes.map((n) =>
          n.name === selectedNode.name ? response.data : n
        )
      );
      setSelectedNode(response.data);
      // 1. Recharge les nœuds côté parent
      await fetchNodes();
      // 2. Puis rafraîchis le graphe visuel (quand les props sont à jour)
      if (graphRefreshFn && typeof graphRefreshFn === "function") {
        setTimeout(() => graphRefreshFn(), 100);
      }
    } catch (error) {
      toast.error(t('featureManager.alerts.nodeUpdateError') + error.message);
    }
  };
  // Modifier
  const handleEditNode = (node) => {
    setEditingPendingNode(node);
  };

  // Sauvegarder la modification
  const savePendingNodeEdit = (updatedNode) => {
    setPendingNodes((prev) =>
      prev.map((n) => (n.id === updatedNode.id ? updatedNode : n))
    );
    setEditingPendingNode(null);
  };

  // Annuler l'édition
  const cancelPendingNodeEdit = () => setEditingPendingNode(null);

  // Supprimer
  const handleDeleteNode = (id) => {
    setPendingNodes((prev) => prev.filter((n) => n.id !== id));
  };
  /*
  useEffect(() => {
    const stored = localStorage.getItem("pendingNodes");
    if (stored) setPendingNodes(JSON.parse(stored));
  }, []);



  useEffect(() => {
    localStorage.setItem("pendingNodes", JSON.stringify(pendingNodes));
  }, [pendingNodes]);
*/
  const [previewNode, setPreviewNode] = useState(null);
  // const [showPreview, setShowPreview] = useState(false);
  // const [previewError, setPreviewError] = useState("");

  const [impactRadarResult, setImpactRadarResult] = useState(null);
  const [impactTypeFilter, setImpactTypeFilter] = useState("");
  const [pendingConfirmation, setPendingConfirmation] = useState(null);
  
  // 🆕 État pour la modal d'enrichissement (ancien système - conservé)
  const [enrichmentProposal, setEnrichmentProposal] = useState(null);
  const [enrichmentLoading, setEnrichmentLoading] = useState(false);

  // 🔍 NOUVEAU - Hook de gestion des conflits (Vector Echo)
  const { conflict, setConflict, isResolving, createNodeWithConflictHandling, resolveConflict } = useConflictResolution();

  // Fonction générique pour appeler l'API Impact Radar
  async function handleImpactRadar(change) {
    setLoadingImpactRadar(true);
    try {
      const res = await apiClient.post(`/impact-analyze`, {
        change,
        lang: i18n.language
      });
      setImpactRadarResult(res.data);

      // 🆕 SAUVEGARDE AUTOMATIQUE de l'analyse dans la session
      try {
        await apiClient.post('/session/impact-radar', {
          data: res.data
        });
        console.log('✅ Analyse Impact Radar sauvegardée automatiquement');
      } catch (saveError) {
        console.warn('⚠️ Erreur sauvegarde Impact Radar:', saveError);
        // Continue même si la sauvegarde échoue
      }

      return res.data;
    } catch (e) {
      toast.error(
        "Erreur lors de l'analyse d'impact: " +
        (e.response?.data?.error || e.message)
      );
      return null;
    } finally {
      setLoadingImpactRadar(false);
    }
  }

  // 🆕 NOUVEAU: Fonction pour charger la dernière analyse d'impact
  const loadLastImpactRadarAnalysis = async () => {
    setLoadingImpactRadar(true);
    try {
      const res = await apiClient.get('/session/impact-radar');
      setImpactRadarResult(res.data);
      console.log('✅ Dernière analyse d\'impact chargée');
    } catch (err) {
      console.warn('⚠️ Aucune analyse d\'impact sauvegardée trouvée:', err);
      toast.info(t('impactRadar.noSavedAnalysis'));
    } finally {
      setLoadingImpactRadar(false);
    }
  };

  const [confirmImpact, setConfirmImpact] = useState(null); // { action: 'delete', nodeName: ... }
  
  // 👈 NEW: Référence pour les fonctions de contrôle des filtres
  const [graphFilters, setGraphFilters] = useState(null);

  // --- DMBT Stepper Logic (identique Header.js, mais local à FeatureManager) ---
  const DMBT_STEPS = [
    { id: 'section-create-node', label: t('featureManager.steps.create') },
    { id: 'section-create-relation', label: t('featureManager.steps.connect') },
    { id: 'section-analysis', label: t('featureManager.steps.analyze') },
    { id: 'section-tests-global', label: t('featureManager.steps.test') },
    { id: 'section-improve', label: t('featureManager.steps.improve') },
  ];
  const [activeStep, setActiveStep] = useState(0);
  // Preview state for Nodemented funnel and node creation
  const [showPreview, setShowPreview] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  useEffect(() => {
    const handleScroll = () => {
      let found = 0;
      for (let i = 0; i < DMBT_STEPS.length; i++) {
        const el = document.getElementById(DMBT_STEPS[i].id);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top < window.innerHeight * 0.33) {
            found = i;
          }
        }
      }
      setActiveStep(found);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const handleStepClick = (step, idx) => {
    const el = document.getElementById(step.id);
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 110;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
    setActiveStep(idx);
  };

  // Nouvel état pour le spinner du bouton Impact Radar
  const [loadingImpactRadar, setLoadingImpactRadar] = useState(false);

  // 📱 Si on est sur mobile, utiliser l'interface mobile simplifiée (consultation seulement)
  if (isMobile) {
    return (
      <MobileLabViewSimple
        // Props de base (lecture seule)
        user={user}
        nodes={nodes}
        allRelationships={allRelationships}
        testScenarios={testScenarios}
        nodeTests={nodeTests}
        selectedNode={selectedNode}
        setSelectedNode={setSelectedNode}
        onNodeSelect={selectNode}
        onBackToDashboard={onBackToDashboard}
        loading={loading}
      />
    );
  }

  return (
    <Card>
      <div className="labNodemented main-section-bg">
        {/* Header Lab fusionné - simplifié */}
        <div className="lab-header-unified">
          <div className="lab-header-left">
            <button
              onClick={onBackToDashboard}
              className="back-to-home-btn"
              title="Back to Home"
            >
              ← 
            </button>
            <div className="lab-title-group">
              <h2 className="lab-main-title">{t('featureManager.title')}</h2>
              <p className="lab-subtitle">{t('featureManager.subtitle')}</p>
            </div>
          </div>

          <div className="lab-header-actions">
            <PlaybooksTrigger onClick={() => setShowPlaybooks(true)} />
            <DMBTTrigger
              onClick={handleOpenDMBTGuide}
              className="action-with-tooltip"
              data-tooltip={t('featureManager.actions.dmbtGuide')}
            />

            {/* LaboratoryTrigger: masqué quand PersonaGuide est actif (doublon) */}
            {!isPersonaGuideActive && (
              <LaboratoryTrigger
                onClick={() => {
                  // smooth scroll to LabNavigation
                  const node = labNavRef.current;
                  if (node) {
                    node.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }}
                className="action-with-tooltip"
                data-tooltip={t('featureManager.actions.laboratoryTooltip')}
              />
            )}

            {/* NodementedTrigger: masqué quand PersonaGuide est actif (doublon) */}
            {!isPersonaGuideActive && (
              <NodementedTriggerBalanced
                loading={loading}
                onClick={() => setShowNodementedFunnel(true)}
                nodes={nodes}
                className={`action-with-tooltip ${nodes.length === 0 ? 'recommended-action' : ''}`}
                data-tooltip={nodes.length === 0 ? t('featureManager.actions.nodemented') : t('featureManager.actions.nodementedActive')}
                data-recommended-text={t('featureManager.actions.recommended')}
              />
            )}

            {/* CGU button removed from lab header - CGU available in dashboard */}
          </div>
        </div>

        {/* ===== POST-IMPORT CTA BANNER ===== */}
        {postImportBanner && (
          <div className="post-import-cta-banner">
            <span className="pic-icon">📥</span>
            <div className="pic-text">
              <strong>Import terminé !</strong>
              <span>Générez des scénarios de test pour les nœuds importés</span>
            </div>
            <button
              className="pic-btn-primary"
              onClick={() => { setPostImportBanner(null); openLabSection('section-tests'); }}
            >
              🧪 Générer des tests →
            </button>
            <button className="pic-btn-dismiss" onClick={() => setPostImportBanner(null)}>✕</button>
          </div>
        )}

        {/* ===== INSIGHT ENGINE PANEL — toujours visible (idle → loading → insights) ===== */}
        {insightsVisible && (
          <InsightPanel
            insights={insights}
            loading={insightsLoading}
            onDismiss={() => setInsightsVisible(false)}
            onRefresh={handleRefreshInsights}
            onNodeSelect={(nodeName) => {
              const found = nodes.find(n => n.name === nodeName || n.id === nodeName);
              if (found) setSelectedNode(found);
            }}
            onSectionNav={(sectionId) => {
              // Ouvre la section sans fermer le panel insight
              const legacyMap = {
                'section-graph': 'relations', 'section-tests': 'tests',
                'section-quality': 'analysis', 'section-specs': 'specifications',
                'section-impact-radar': 'analysis',
              };
              const rawId = (sectionId ?? '').toString();
              const lower = rawId.toLowerCase();
              const normalizedId = legacyMap[rawId]
                || (lower === 'topology' ? 'topology' : null)
                || (lower.includes('test') ? 'tests' : null)
                || (lower.includes('spec') ? 'specifications' : null)
                || ((lower.includes('relation') || lower.includes('graph')) ? 'relations' : null)
                || ((lower.includes('vector') || lower.includes('search')) ? 'vectorSearch' : null)
                || ((lower.includes('impact') || lower.includes('analysis') || lower.includes('quality') || lower.includes('risk') || lower.includes('hub') || lower.includes('central') || lower.includes('isolated')) ? 'topology' : null)
                || rawId;
              openLabSection(normalizedId);
            }}
            onActionClick={(action) => {
              switch (action.type) {
                case 'create-test':
                  // Pré-sélectionner le nœud cible et ouvrir Tests
                  if (action.data?.targetNode) {
                    const found = nodes.find(n => n.name === action.data.targetNode);
                    if (found) setSelectedNode(found);
                  }
                  openLabSection('tests');
                  break;
                case 'create-node':
                  openLabSection('createNode');
                  break;
                case 'add-relation':
                  openLabSection('relations');
                  break;
                case 'navigate':
                  if (action.target?.nodeId) {
                    const found = nodes.find(n => n.name === action.target.nodeId || n.id === action.target.nodeId);
                    if (found) setSelectedNode(found);
                  }
                  break;
                default:
                  console.warn('[InsightEngine] Action inconnue:', action.type);
              }
            }}
            onSectionClick={(sectionId) => {
              const legacyMap = {
                'section-graph':        'relations',
                'section-tests':        'tests',
                'section-quality':      'analysis',
                'section-specs':        'specifications',
                'section-impact-radar': 'analysis',
              };
              const rawId = (sectionId ?? '').toString();
              const lower = rawId.toLowerCase();
              const normalizedId = legacyMap[rawId]
                || (lower.includes('test') ? 'tests' : null)
                || (lower.includes('spec') ? 'specifications' : null)
                || ((lower.includes('relation') || lower.includes('graph')) ? 'relations' : null)
                || ((lower.includes('vector') || lower.includes('search')) ? 'vectorSearch' : null)
                || ((lower.includes('impact') || lower.includes('analysis') || lower.includes('quality') || lower.includes('risk')) ? 'analysis' : null)
                || rawId;

              const allowedSections = new Set([
                'tests',
                'analysis',
                'specifications',
                'relations',
                'vectorSearch',
                'topology',
              ]);

              if (!allowedSections.has(normalizedId)) {
                console.warn('[InsightEngine] Section inconnue:', sectionId);
                return;
              }

              setInsightsVisible(false);
              openLabSection(normalizedId);
            }}
          />
        )}

        {/* Section Graph - Vue principale toujours visible (moved to top) */}
        <GraphSection
          nodes={nodes}
          allRelationships={allRelationships}
          selectedNode={selectedNode}
          setSelectedNode={setSelectedNode}
          setGraphRefreshFn={setGraphRefreshFn}
          loading={loading}
          generateTests={generateTests}
          setTestScenarios={setTestScenarios}
          setLoading={setLoading}
          API_BASE_URL={API_BASE_URL}
          onRefreshData={fetchNodes}
          openLabSection={openLabSection}
          user={user}
          onOpenTestGeneration={() => setShowTestGenerationModal(true)}
          onFilterCleanup={(cleanupFn) => {
            setGraphFilters({ cleanupTeamFilters: cleanupFn });
          }}
        />

        {/* Système de navigation avec activeSection restauré */}
        {/* 🎯 PersonaGuide — parcours guidé par persona (feature flag) */}
        {isPersonaGuideActive && (
          <div ref={personaGuideRef}>
            <PersonaGuide
              persona={activePersona}
              activeSection={activeSection}
              onOpenSection={openLabSection}
              onDismiss={() => setShowPersonaGuide(false)}
            />
          </div>
        )}
        <div ref={labNavRef}>
          <LabNavigation
            onOpenSection={openLabSection}
            activeSection={activeSection}
            selectedNode={selectedNode}
            nodes={nodes}
            testScenarios={testScenarios}
            relationships={allRelationships}
            pendingNodes={pendingNodes}
          />
        </div>
        {showNodementedFunnel && (
          <NodementedFunnelModalSimple
            isOpen={showNodementedFunnel}
            onClose={() => {
              console.log('Funnel Nodemented fermé');
              setShowNodementedFunnel(false);
              fetchNodes(); // Actualiser les données après fermeture
            }}
            user={user}
            nodes={nodes}
            addPendingNode={addPendingNode}
            pendingNodes={pendingNodes}
            handleValidateNode={handleValidateNode}
            handleEditNode={handleEditNode}
            handleDeleteNode={handleDeleteNode}
            savePendingNodeEdit={savePendingNodeEdit}
            editingPendingNode={editingPendingNode}
            cancelPendingNodeEdit={cancelPendingNodeEdit}
            setNodes={setNodes}
            loading={loading}
            setLoading={setLoading}
            nodeName={nodeName}
            setNodeName={setNodeName}
            nodeType={nodeType}
            setNodeType={setNodeType}
            technology={technology}
            setTechnology={setTechnology}
            status={status}
            setStatus={setStatus}
            nodeDescription={nodeDescription}
            setNodeDescription={setNodeDescription}
            metadata={metadata}
            setMetadata={setMetadata}
            endpointSchema={endpointSchema}
            setEndpointSchema={setEndpointSchema}
            saveNode={saveNode}
            updateNodeSimple={updateNodeSimple}
            resetForm={resetForm}
            previewNode={previewNode}
            setPreviewNode={setPreviewNode}
            showPreview={showPreview}
            setShowPreview={setShowPreview}
            previewError={previewError}
            setPreviewError={setPreviewError}
          />
        )}



        {/* ===== MODAL SECTION LAB (LOGIQUE RESTAURÉE) ===== */}
        {activeSection === 'createNode' && (
          <LabSectionModal
            isOpen={true}
            onClose={closeLabSection}
            sectionId="createNode"
            title={t('featureManager.createNode.title') || 'Créer un Nœud'}
            icon="🔧"
            size="large"
          >
            <CreateNodeSection
              selectedNode={selectedNode}
              resetSelection={resetSelection}
              loading={loading}
              nodeName={nodeName}
              setNodeName={setNodeName}
              nodeType={nodeType}
              setNodeType={setNodeType}
              technology={technology}
              setTechnology={setTechnology}
              featureTeam={featureTeam}           // 👈 NEW: Pass featureTeam
              setFeatureTeam={setFeatureTeam}     // 👈 NEW: Pass setFeatureTeam
              status={status}
              setStatus={setStatus}
              nodeDescription={nodeDescription}
              setNodeDescription={setNodeDescription}
              metadata={metadata}
              setMetadata={setMetadata}
              endpointSchema={endpointSchema}
              setEndpointSchema={setEndpointSchema}
              saveNode={saveNode}
              updateNodeSimple={updateNodeSimple}
              resetForm={resetForm}
              previewNode={previewNode}
              setPreviewNode={setPreviewNode}
              showPreview={showPreview}
              setShowPreview={setShowPreview}
              previewError={previewError}
              setPreviewError={setPreviewError}
              addPendingNode={addPendingNode}
              nodes={nodes}
              API_BASE_URL={API_BASE_URL}
              setLoading={setLoading}
              generateTests={generateTests}
            />
          </LabSectionModal>
        )}

        {activeSection === 'nodesList' && (
          <LabSectionModal
            isOpen={true}
            onClose={closeLabSection}
            sectionId="nodesList"
            title={t('featureManager.nodesList.title') || 'Liste des Nœuds'}
            icon="📋"
            size="fullscreen"
          >
            <div className="space-y-4">
            

              <NodesList
                nodes={currentNodes}
                selectNode={selectNode}
                selectedNode={selectedNode}
                toggleNodeSelection={toggleNodeSelection}
                deleteNode={deleteNode}
                loading={loading}
                loadingNode={loadingNode}
                relationships={allRelationships}
                onEditNode={(node) => {
                  selectNode(node);
                  closeLabSection();
                  openLabSection('createNode');
                }}
                onViewDetails={(node) => {
                  selectNode(node);
                  closeLabSection();
                  openLabSection('createNode');
                }}
                onShowTests={(node) => {
                  selectNode(node);
                  closeLabSection();
                  openLabSection('tests');
                }}
                onShowRelations={(node) => {
                  selectNode(node);
                  closeLabSection();
                  openLabSection('relations');
                }}
                onOpenImpactRadar={(analysis) => {
                  setImpactRadarResult(analysis);
                }}
              />

              <NodesPagination
                nodesPerPage={nodesPerPage}
                totalNodes={filteredNodes.length}
                paginate={paginate}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
              />
            </div>
          </LabSectionModal>
        )}

        {activeSection === 'relations' && (
          <LabSectionModal
            isOpen={true}
            onClose={closeLabSection}
            sectionId="relations"
            title={t('featureManager.relations.title') || 'Gestion des Relations'}
            icon="🔗"
            size="large"
          >
            <div className="space-y-6">
              <RelationCreator
                nodes={nodes}
                setNodes={setNodes}
                selectedNode={selectedNode}
                targetNode={targetNode}
                setTargetNode={setTargetNode}
                targetNodeSearch={targetNodeSearch}
                setTargetNodeSearch={setTargetNodeSearch}
                relationshipType={relationshipType}
                setRelationshipType={setRelationshipType}
                customRelationshipType={customRelationshipType}
                setCustomRelationshipType={setCustomRelationshipType}
                relationships={allRelationships}
                addRelationship={addRelationship}
                loading={loading}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                apiClient={apiClient}
              />

              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold mb-4">{t('relations.existing')}</h3>
                <RelationsList
                  relationships={currentRelationships}
                  nodes={nodes}
                  deleteRelationship={deleteRelationship}
                  setExpandedRelation={setExpandedRelation}
                  loading={loading}
                />

                <RelationsPagination
                  relationsPerPage={relationsPerPage}
                  totalRelations={allRelationships.length}
                  paginate={(pageNumber) => setCurrentRelationshipPage(pageNumber)}
                  currentPage={currentRelationshipPage}
                  setCurrentPage={setCurrentRelationshipPage}
                />
              </div>
            </div>
          </LabSectionModal>
        )}

        {activeSection === 'importCode' && (
          <LabSectionModal
            isOpen={true}
            onClose={closeLabSection}
            sectionId="importCode"
            title={t('featureManager.importCode.title') || 'Import de Code'}
            icon="📁"
            size="large"
          >
            <div>
              {/* Use the existing GraphFromCode section to preserve previous UX */}
              <GraphBootstrapFromCode />
            </div>
          </LabSectionModal>
        )}

        {activeSection === 'quickCreate' && (
          <LabSectionModal
            isOpen={true}
            onClose={closeLabSection}
            sectionId="quickCreate"
            title={t('featureManager.quickCreate.title') || 'Création Rapide'}
            icon="⚡"
            size="large"
          >
            <div>
              {/* Use the original TextToNodeCreator to restore prior layout and behavior */}
              <TextToNodeCreator onNodeCreated={fetchNodes} addPendingNode={addPendingNode} />
            </div>
          </LabSectionModal>
        )}

        {activeSection === 'tests' && (
          <LabSectionModal
            isOpen={true}
            onClose={closeLabSection}
            sectionId="tests"
            title={t('featureManager.tests.title') || 'Tests et Validation'}
            icon="🧪"
            size="large"
          >
            <GlobalTestsSection
              nodes={nodes}
              relationships={allRelationships}
              nodeTests={nodeTests}
              testScenarios={testScenarios}
              setTestScenarios={setTestScenarios}
              selectedNode={selectedNode}
              selectedTestScenario={selectedTestScenario}
              setSelectedTestScenario={setSelectedTestScenario}
              duplicateTestIds={duplicateTestIds}
              extractStepsByType={extractStepsByType}
              removeDuplicateTest={removeDuplicateTest}
              handleUpdateScenario={handleUpdateScenario}
              handleOpenManualTestCreator={handleOpenManualTestCreator}
              handleOpenTestGeneration={handleOpenTestGeneration}
              generateTests={generateTests}
              fetchNodes={fetchNodes}
              addPendingNode={addPendingNode}
              loading={loading}
              setLoading={setLoading}
              autoLoadTests={autoLoadTests}
              setAutoLoadTests={setAutoLoadTests}
            />
          </LabSectionModal>
        )}

        {activeSection === 'exploratoryTests' && (
          <LabSectionModal
            isOpen={true}
            onClose={closeLabSection}
            sectionId="exploratoryTests"
            title="🤖 Tests Exploratoires FARA-7B"
            icon="🤖"
            size="large"
          >
            <ExploratoryTestRunner onClose={closeLabSection} />
          </LabSectionModal>
        )}

        {activeSection === 'regressionRadar' && (
          <LabSectionModal
            isOpen={true}
            onClose={closeLabSection}
            sectionId="regressionRadar"
            title="📡 Regression Radar"
            icon="📡"
            size="large"
          >
            <RegressionRadar />
          </LabSectionModal>
        )}

        {activeSection === 'vectorSearch' && (
          <LabSectionModal
            isOpen={true}
            onClose={closeLabSection}
            sectionId="vectorSearch"
            title="🤖 Quality Assistant and Vector Search"
            icon="🔍"
            size="large"
          >
            <QualityAssistant
              nodes={nodes}
              onNodeSelect={selectNode}
              addPendingNode={addPendingNode}
              onHighlightNodes={(nodeNames) => {
                // Highlight nœuds dans le graphe
                const nodesToHighlight = nodes.filter(n => nodeNames.includes(n.name));
                if (nodesToHighlight.length > 0) {
                  selectNode(nodesToHighlight[0]);
                  toast.info(`🎯 ${nodesToHighlight.length} nœud(s) mis en évidence`);
                }
              }}
              onNavigateToSection={(section) => {
                // Navigation vers section
                setActiveSection(section);
              }}
            />
          </LabSectionModal>
        )}

        {activeSection === 'analysis' && (
          <LabSectionModal
            isOpen={true}
            onClose={closeLabSection}
            sectionId="analysis"
            title={t('featureManager.analysis.title') || 'Analyses et Rapports'}
            icon="📊"
            size="fullscreen"
          >
            <div className="space-y-6">
              <TestCoverageTable
                nodes={nodes}
                testScenarios={testScenarios}
                relationships={allRelationships}
              />

              <GlobalTestsGraph
                nodes={nodes}
                relationships={allRelationships}
                testScenarios={testScenarios}
              />
            </div>
          </LabSectionModal>
        )}

        {activeSection === 'specifications' && (
          <LabSectionModal
            isOpen={true}
            onClose={closeLabSection}
            sectionId="specifications"
            title={t('featureManager.specifications.title') || 'Spécifications'}
            icon="📋"
            size="large"
          >
            <SpecificationsSection
              selectedNode={selectedNode}
              nodes={nodes}
              onOpenSpecificationGenerator={handleOpenSpecificationGenerator}
              onGenerateSpecification={handleGenerateSpecification}
              onExportSpecification={handleExportSpecification}
              onDeleteSpecification={handleDeleteSpecification}
              refreshSpecifications={specificationsRefreshRef}
              specificationTemplates={[
                { id: 'api', name: 'API Documentation', icon: '🔌' },
                { id: 'user-story', name: 'User Story', icon: '👤' },
                { id: 'technical', name: 'Technical Spec', icon: '⚙️' },
                { id: 'test-plan', name: 'Test Plan', icon: '🧪' },
                { id: 'architecture', name: 'Architecture Doc', icon: '🏗️' }
              ]}
              loading={loading}
              t={t}
            />
          </LabSectionModal>
        )}

        {/* ===== SECTION TOPOLOGY ANALYSIS ===== */}
        {activeSection === 'topology' && (
          <LabSectionModal
            isOpen={true}
            onClose={closeLabSection}
            sectionId="topology"
            title="🔬 Analyse Topologique"
            icon="🔬"
            size="fullscreen"
          >
            <TopologyPanel
              topology={insights?.topology}
              onNodeSelect={(nodeName) => {
                const found = nodes.find(n => n.name === nodeName || n.id === nodeName);
                if (found) setSelectedNode(found);
              }}
              onOpenTests={(node) => {
                const found = nodes.find(n => n.name === node.name || n.id === node.name);
                if (found) setSelectedNode(found);
                closeLabSection();
                openLabSection('tests');
              }}
              onOpenRelations={(node) => {
                const found = nodes.find(n => n.name === node.name || n.id === node.name);
                if (found) setSelectedNode(found);
                closeLabSection();
                openLabSection('relations');
              }}
              onOpenImpactRadar={async (node) => {
                const found = nodes.find(n => n.name === node.name || n.id === node.name);
                if (!found) {
                  toast.warning(`Nœud "${node.name}" non trouvé dans le graphe local. Rafraîchissez la page.`);
                  return;
                }
                setSelectedNode(found);
                closeLabSection();
                const impact = await handleImpactRadar({
                  name: found.name,
                  type: 'modification',
                  description: `Analyse d'impact topologique pour ${found.name}`,
                  scenarios: [],
                });
                if (!impact) setImpactRadarResult(null);
              }}
            />
          </LabSectionModal>
        )}

        {/* ===== SECTION JIRA IMPORT ===== */}
        {activeSection === 'jiraImport' && (
          <LabSectionModal
            isOpen={true}
            onClose={closeLabSection}
            sectionId="jiraImport"
            title={t('featureManager.jiraImport.title') || 'Import JIRA'}
            icon="🔄"
            size="large"
          >
            {integrations?.atlassian?.connected ? (
              <div style={{ padding: '20px' }}>
                <JiraAnalyzerModal
                  isOpen={true}
                  onClose={closeLabSection}
                  onNodesGenerated={(data) => {
                    // Ajouter les nodes aux pendingNodes
                    if (data.nodes && data.nodes.length > 0) {
                      data.nodes.forEach(node => {
                        addPendingNode({
                          ...node,
                          source: 'JIRA',
                          jiraTicket: data.nodes[0].jiraTicket
                        });
                      });
                      toast.success(`${data.nodes.length} nœud(s) ajouté(s) aux ressources en attente`);
                    }
                    closeLabSection();
                  }}
                />
              </div>
            ) : (
              <IntegrationPlaceholder 
                provider="atlassian"
                onConfigure={() => {
                  setSelectedProvider('atlassian');
                  setShowIntegrationSetup(true);
                }}
              />
            )}
          </LabSectionModal>
        )}

        {/* ===== SECTION TEST CYCLES - ZephyrMented ===== */}
        {activeSection === 'testCycles' && (
          <LabSectionModal
            isOpen={true}
            onClose={closeLabSection}
            sectionId="testCycles"
            title={t('featureManager.testCycles.title') || 'Test Cycles'}
            icon="🔄"
            size="xlarge"
          >
            <TestCycleManager />
          </LabSectionModal>
        )}

        {/* ===== SECTION DUPLICATE DETECTOR ===== */}
        {activeSection === 'duplicateDetector' && (
          <LabSectionModal
            isOpen={true}
            onClose={closeLabSection}
            sectionId="duplicateDetector"
            title={t('duplicateDetectorTitle') || 'Détecteur de Doublons'}
            icon="🔍"
            size="xlarge"
          >
            <div style={{ padding: '20px', height: '80vh', overflow: 'auto' }}>
              <SmartDuplicateDetector />
            </div>
          </LabSectionModal>
        )}

        {/* ===== SECTION COVERAGE ADVISOR ===== */}
        {activeSection === 'coverageAdvisor' && (
          <LabSectionModal
            isOpen={true}
            onClose={closeLabSection}
            sectionId="coverageAdvisor"
            title={t('coverageAdvisorTitle') || 'Conseiller de Couverture'}
            icon="📊"
            size="xlarge"
          >
            <div style={{ padding: '20px', height: '80vh', overflow: 'auto' }}>
              <TestCoverageAdvisor 
                onNavigateToTestGenerator={(nodeId, nodeName) => {
                  // Trouver le nœud et ouvrir la modal TestGeneration
                  const targetNode = nodes.find(n => Number(n.id) === Number(nodeId) || n.name === nodeName);
                  if (targetNode) {
                    setSelectedNode(targetNode);
                    localStorage.setItem('selectedNode', JSON.stringify(targetNode));
                    setShowTestGenerationModal(true);
                    closeLabSection();
                  }
                }}
              />
            </div>
          </LabSectionModal>
        )}

        {/* ===== SECTION RELATED FEATURES FINDER ===== */}
        {activeSection === 'relatedFinder' && (
          <LabSectionModal
            isOpen={true}
            onClose={closeLabSection}
            sectionId="relatedFinder"
            title={t('relatedFinderTitle') || 'Découvreur de Features'}
            icon="🔗"
            size="xlarge"
          >
            <div style={{ padding: '20px', height: '80vh', overflow: 'auto' }}>
              <RelatedFeaturesFinder />
            </div>
          </LabSectionModal>
        )}

        {/* ===== SECTION PENDING NODES (VALIDATION) ===== */}
        {activeSection === 'pendingNodes' && (
          <LabSectionModal
            isOpen={true}
            onClose={closeLabSection}
            sectionId="pendingNodes"
            title={t('featureManager.pendingNodes.title')}
            icon="⚡"
            size="fullscreen"
          >
            <PendingNodesPanel
              pendingNodes={pendingNodes}
              nodes={nodes}
              handleValidateTestNode={handleValidateTestNode}
              handleValidateNode={handleValidateNode}
              handleEditNode={handleEditNode}
              handleDeleteNode={handleDeleteNode}
              onUpdatePendingNode={savePendingNodeEdit}
              onEditManualTest={handleEditManualTest}
            />
          </LabSectionModal>
        )}

        {/* ===== SECTION EXPORT ===== */}
        {activeSection === 'export' && (
          <ExportManagerStyled
            nodes={nodes}
            relations={allRelationships}
            tests={nodeTests}
            defaultOpen={true}
            onModalClose={closeLabSection}
          />
        )}

      </div> {/* Fin de labNodemented main-section-bg */}
      {  /*    <Card>
        <h2 className="text-xl font-bold mb-4">Gestion des Nœuds & Relations</h2>
        <div className="my-6 border-t border-gray-300"></div>
        <Header selectedNode={selectedNode} /> 

      
        <div id="section-assistant" className="my-6">
          <h3 className="text-lg font-semibold mb-4">
            💬 Assistant Intelligent
          </h3>
          <ConversationAssistant
            nodes={nodes}
            onNodeSelect={setSelectedNode}
          />
        </div>



     {/*   <div id="section-impact-analyzer" className="my-6">
          <h3 className="text-lg font-semibold mb-4">
            🔍 Analyse d'Impact
          </h3>
          <ImpactAnalyzer
            nodes={nodes}
            onNodeSelect={setSelectedNode}
          />
        </div>
*/}
      <BackToTop />
      {/* Ajouter le modal d'édition */}
      <Modal open={!!editingPendingNode} onClose={cancelPendingNodeEdit} className="p-6 max-w-3xl w-full relative animate-fadeIn">
        {editingPendingNode && (
          <>
            <h3 className="text-lg font-bold mb-4">{t('featureManager.editNode')}</h3>
            <button onClick={cancelPendingNodeEdit} className="absolute top-2 right-2 text-gray-600 hover:text-black">✖</button>
            <form onSubmit={(e) => { e.preventDefault(); savePendingNodeEdit(editingPendingNode); }}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-gray-700 text-sm font-bold mb-2">Nom</label>
                  <input className="shadow border rounded w-full py-2 px-3 text-gray-700" value={editingPendingNode.name} onChange={(e) => setEditingPendingNode({ ...editingPendingNode, name: e.target.value })} placeholder={t('placeholders.nodeName')} required />
                </div>
                <div>
                  <label className="block text-gray-700 text-sm font-bold mb-2">Type</label>
                  <input className="shadow border rounded w-full py-2 px-3 text-gray-700" value={editingPendingNode.type} onChange={(e) => setEditingPendingNode({ ...editingPendingNode, type: e.target.value })} placeholder={t('placeholders.type')} required />
                </div>
                <div>
                  <label className="block text-gray-700 text-sm font-bold mb-2">Auteur</label>
                  <input className="shadow border rounded w-full py-2 px-3 text-gray-700" value={editingPendingNode.author || ""} onChange={(e) => setEditingPendingNode({ ...editingPendingNode, author: e.target.value })} placeholder={t('placeholders.author')} />
                </div>
                <div>
                  <label className="block text-gray-700 text-sm font-bold mb-2">Technologie</label>
                  <input className="shadow border rounded w-full py-2 px-3 text-gray-700" value={editingPendingNode.technology || ""} onChange={(e) => setEditingPendingNode({ ...editingPendingNode, technology: e.target.value })} placeholder={t('placeholders.technology')} />
                </div>
                <div className="col-span-2">
                  <label className="block text-gray-700 text-sm font-bold mb-2">Description</label>
                  <textarea className="shadow border rounded w-full py-2 px-3 text-gray-700" value={editingPendingNode.description} onChange={(e) => setEditingPendingNode({ ...editingPendingNode, description: e.target.value })} placeholder={t('placeholders.description')} rows="4" />
                </div>
              </div>
              <div className="flex justify-end space-x-2">
                <button type="button" onClick={cancelPendingNodeEdit} className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300">{t('featureManager.cancel')}</button>
                <button type="submit" className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600">{t('actions.save')}</button>
              </div>
            </form>
          </>
        )}
      </Modal>
      <ImpactRadarModal
        open={!!impactRadarResult || loadingImpactRadar}
        onClose={() => {
          setImpactRadarResult(null);
          setLoadingImpactRadar(false);
          setPendingConfirmation(null); // Annuler la confirmation en attente
        }}
        impactRadarResult={impactRadarResult}
        impactTypeFilter={impactTypeFilter}
        setImpactTypeFilter={setImpactTypeFilter}
        pendingNodes={pendingNodes}
        onConfirmModification={pendingConfirmation ? pendingConfirmation.action : null}
        pendingModificationText={
          pendingConfirmation?.type === 'updateNode'
            ? t('featureManager.buttons.continueUpdate')
            : t('featureManager.buttons.continueModification')
        }
      />
      <Modal open={!!confirmImpact} onClose={() => setConfirmImpact(null)} className="p-6 max-w-md w-full relative animate-fadeIn">
        {confirmImpact && (
          <>
            <h3 className="text-lg font-bold mb-4">{t('featureManager.confirmDeletion')}</h3>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setConfirmImpact(null)} className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300">{t('featureManager.cancelAndShowImpacts')}</button>
              <button
                onClick={async () => {
                  setLoading(true);
                  try {
                    await apiClient.delete(`/node/${confirmImpact.nodeName}`);
                    setNodes(nodes.filter((n) => n.name !== confirmImpact.nodeName));
                    
                    // 👈 NEW: Nettoyer les filtres après suppression
                    if (graphFilters?.cleanupTeamFilters) {
                      setTimeout(() => {
                        graphFilters.cleanupTeamFilters();
                      }, 100); // Petit délai pour laisser l'état se mettre à jour
                    }
                  } catch (error) {
                    toast.error("Erreur lors de la suppression du nœud: " + error.message);
                  }
                  setLoading(false);
                  setConfirmImpact(null);
                }}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Supprimer
              </button>
            </div>
          </>
        )}
      </Modal>

      {/* Modal pour le créateur de tests manuels */}
      <Modal
        open={showManualTestCreator}
        onClose={handleCancelManualTest}
        className="manual-test-modal-overlay"
      >
        <ManualTestCreator
          onSave={handleSaveManualTest}
          onCancel={handleCancelManualTest}
          availableNodes={nodes}
          initialData={editingManualTest}
          selectedNode={selectedNode}
          addPendingNode={addPendingNode}
        />
      </Modal>

      {/* <TemplateGuide /> */}

      {/* Modal pour le générateur de spécifications IA */}
      <SpecificationGenerator
        isOpen={showSpecificationGenerator}
        onClose={handleCloseSpecificationGenerator}
        onSpecificationGenerated={handleSpecificationGenerated}
        onSpecificationSaved={() => {
          // Déclencher le refresh de la liste des spécifications
          if (specificationsRefreshRef.current) {
            console.log('🔄 Rafraîchissement de la liste des spécifications après sauvegarde...');
            specificationsRefreshRef.current();
          }
        }}
        existingFeatures={nodes}
        user={user}
      />

      {/* Modal pour le guide DMBT */}
      <DMBTGuide
        isOpen={showDMBTGuide}
        onClose={handleCloseDMBTGuide}
      />

      {/* Modal Playbooks */}
      <Playbooks
        isOpen={showPlaybooks}
        onClose={() => setShowPlaybooks(false)}
      />

      {/* Workflow guide removed: replaced by smooth-scroll to LabNavigation (Primary Sections) */}

      {/* Modal pour les détails de relation */}
      {expandedRelation && (
        <div className="modal-overlay" onClick={() => setExpandedRelation(null)}>
          <div className="modal-content relationship-detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{t('featureManager.relationDetails')}</h3>
              <button 
                className="modal-close-btn"
                onClick={() => setExpandedRelation(null)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="relationship-detail-visualization">
                <div className="detail-source-node">
                  <div className="detail-node-circle">
                    <span className="detail-node-icon">📄</span>
                  </div>
                  <div className="detail-node-info">
                    <div className="detail-node-label">{expandedRelation.nodeName1}</div>
                    <div className="detail-node-type">{t('graphFromCodePreview.source')}</div>
                  </div>
                </div>
                
                <div className="relationship-detail-arrow">
                  <div className="detail-arrow-line"></div>
                  <div className="detail-arrow-type">{expandedRelation.relationshipType}</div>
                </div>
                
                <div className="detail-target-node">
                  <div className="detail-node-circle">
                    <span className="detail-node-icon">📄</span>
                  </div>
                  <div className="detail-node-info">
                    <div className="detail-node-label">{expandedRelation.nodeName2}</div>
                    <div className="detail-node-type">{t('graphFromCodePreview.target')}</div>
                  </div>
                </div>
              </div>
              
              {expandedRelation.description && (
                <div className="relation-info-section">
                  <h4>{t('graphFromCodePreview.description')}</h4>
                  <p>{expandedRelation.description}</p>
                </div>
              )}
              
              {expandedRelation.confidence && (
                <div className="relation-info-section">
                  <h4>{t('smartActionButton.confidence')}</h4>
                  <p>{expandedRelation.confidence}</p>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button 
                className="btn btn-secondary"
                onClick={() => setExpandedRelation(null)}
              >
                {t('close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 📜 Modal CGU pour l'acceptation obligatoire */}
      <CGUModal
        isOpen={showCGUModal}
        onAccept={handleCGUAccept}
        onDecline={handleCGUDecline}
        userEmail={userEmail}
      />

      {/* 🧪 Modal de génération de tests */}
      <TestGenerationModal
        isOpen={showTestGenerationModal}
        onClose={() => setShowTestGenerationModal(false)}
        selectedNode={selectedNode}
        generateTests={generateTests}
        fetchNodes={fetchNodes}
        addPendingNode={addPendingNode}
      />

      {/* � Modal d'enrichissement de nœud */}
      <EnrichmentModal
        enrichmentProposal={enrichmentProposal}
        onClose={() => setEnrichmentProposal(null)}
        onConfirm={handleEnrichNode}
        loading={enrichmentLoading}
      />

      {/* Modal de résolution de conflits Vector Echo */}
      {conflict && (
        <ConflictResolutionModal
          conflict={conflict}
          onResolve={handleConflictResolution}
          onCancel={() => setConflict(null)}
          isResolving={isResolving}
        />
      )}

      {/* �📜 Page CGU complète (consultatif) */}
      {showCGUPage && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.8)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '1rem'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            maxWidth: '90vw',
            maxHeight: '90vh',
            overflow: 'auto',
            position: 'relative'
          }}>
            <button
              onClick={handleCloseCGUPage}
              style={{
                position: 'absolute',
                top: '1rem',
                right: '1rem',
                background: '#ef4444',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                cursor: 'pointer',
                fontSize: '1.2rem',
                zIndex: 10000
              }}
            >
              ✕
            </button>
            <CGU showButtons={false} />
          </div>
        </div>
      )}

      {/* 🔌 Integration Setup Modal for Lab JIRA section */}
      {showIntegrationSetup && (
        <IntegrationSetupModal
          provider={selectedProvider}
          isOpen={showIntegrationSetup}
          onClose={() => {
            setShowIntegrationSetup(false);
            setSelectedProvider(null);
          }}
          onSuccess={() => {
            setShowIntegrationSetup(false);
            setSelectedProvider(null);
            // Refresh integrations to update UI immediately
            if (refreshIntegrations) {
              refreshIntegrations();
            }
            toast.success(t('jiraIntegration.setup.success'));
          }}
        />
      )}

      {/* 🛡️ SafeChangeFlow — parcours guidé "changement sûr" (feature flag) */}
      {isFeatureEnabled(FEATURES.SAFE_CHANGE_FLOW) && (
        <SafeChangeFlow
          isOpen={showSafeChangeFlow}
          onClose={() => { setShowSafeChangeFlow(false); if (onSafeChangeFlowClosed) onSafeChangeFlowClosed(); }}
          onSeeTests={(node) => {
            if (node) selectNode(node);
            setShowSafeChangeFlow(false);
            if (onSafeChangeFlowClosed) onSafeChangeFlowClosed();
            openLabSection('tests', true);
          }}
          nodes={nodes}
          selectedNode={selectedNode}
          user={user}
        />
      )}
    </Card>
  );
};

export default FeatureManager;